import {
  Fragment,
  useEffect,
  useLayoutEffect,
  MouseEvent,
  ReactNode,
  RefObject,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";

import { SortAscending, ShareFat, ArrowLeft, BracketsCurly, CaretDoubleDown, CaretLeft, CaretRight, Check, Checks, Code, Copy, DotsThreeVertical, Funnel, FunnelX, Icon, MagnifyingGlass, Paperclip, PaperPlaneTilt, Plus, TextB, TextItalic, TextStrikethrough, Image as ImageIcon, Clock, Microphone, VideoCamera, ChatSlash, File, FileImage, FileVideo, FileAudio, X, MapPin, AddressBook, ArrowBendUpLeft, Smiley, PencilSimple, Trash, Sticker as StickerIcon, NotePencil } from "@phosphor-icons/react";
import { IconButton, Shape } from "../../../components/Buttons/IconButton";
import { RightView, LeftView, ChatsMain, LeftViewHead, ChatListHeadActions, ChatViewEmpty, ChatListTypes, ServicesList, FormAnimated, ChatSearchForm, ChatItem, ServicesScrollBarView, ChatTopBar, ChatView, ChatBottomBar, ChatScrollBarView, DateGroupMessages, MessageLeftRow, MessageRightRow, MessageActions, MessageActionMenu, MessageActionButton, DateGroupTitle, ChatList, ScrollToBottom, InputTextView, SortAscendingIcon, SortDescendingIcon, Toolbar, ChatMenu, Color, ChatMenuItem, AttachmentView, AttachmentInput, AttachmentActions, ReplyPreviewCard, ReplyPreviewMedia, ReplyPreviewContent, ReplyPreviewCloseButton, MessageReactions, MessageReactionPicker, StickerPickerCard, EmojiPickerCard, ServiceSkeletonItem, SkeletonLine, SkeletonMessagesView, SkeletonMessageBubble, ChatContentInfo, InternalMessageRow, ContextPanel, ContextPanelBackdrop, ContextField, ListEmptyState, ForwardedLabel, PersistedFailure, MultiMessageSelectionBar } from "./styles";
import { Select } from "../../../components/Form/Fields/Select";
import { Queue } from "../../../models/Queue";
import { defaultChannel, defaultQueue, useChatController } from "../../../controllers/chatController";
import { Channel } from "../../../models/Channel";
import { Service, ServiceStatus } from "../../../models/Service";
import { Button, Size, Variant } from "../../../components/Buttons";
import { useStore } from "../../../store";
import { AnimatePresence, motion } from "motion/react";
import { Avatar } from "../../../components/Avatar";
import { Message, MessageStatus, MessageType, OwnerTypeMessage } from "../../../models/Message";
import { formatRelativeDate } from "../../../utils/formatRelativeDate";
import { Badge } from "../../../components/Badge";
import { removeFormattingMarksPreservingSpacing } from "../../../utils/channelAndHtml";
import messagesIcon from "../../../assets/icons/send.png";

import { Order } from "../../../services/serviceServices";
import { format } from "date-fns";
import { BubbleMenu } from "@tiptap/react";
import { OutsideClick } from "../../../components/OutSideClick";
import { User, UserRole } from "../../../models/User";
import { ConfirmModal } from "../../../components/Modals/ConfirmModal";
import { TransferServiceModal } from "../../../components/Modals/TransferServiceModal";
import { CloseServiceModal } from "../../../components/Modals/CloseServiceModal";
import { Audio } from "../../../components/Message/Audio";
import { FileMetadata } from "../../../models/FileMetadata";
import { formatDuration } from "../../../utils/time";
import { Image } from "../../../components/Message/Image";
import { Video } from "../../../components/Message/Videos";
import { Document } from "../../../components/Message/Document";
import { Sticker } from "../../../components/Message/Sticker";
import { Location } from "../../../components/Message/Location";
import { SharedContact, SharedContactValue } from "../../../components/Message/SharedContact";
import { Poll } from "../../../components/Message/Poll";
import { Contact } from "../../../models/Contact";
import { CaretDoubleRight } from "@phosphor-icons/react/dist/ssr";
import { buildInlineFileURL } from "../../../utils/fileProxy";
import { canSeeDeletedContent } from "../../../services/messageAdapters";
import { getSpecialMessagePreview } from "../../../utils/specialMessages";
import { ContactModal } from "../../../components/Modals/ContactModal";
import { getStickerReference, removeSavedSticker as removeSavedStickerRequest, saveStickerFromMessage, SavedSticker } from "../../../services/savedStickerServices";
import { resolveLastMessageSenderLabel, resolveMessageSenderAvatarReference, resolveMessageSenderName } from "../../../services/messageAttribution";
import { SpinLoading } from "../../../components/SpinLoading";
import { ImagePreviewContent } from "../../../components/Modals/FileShowModal/ImagePreviewContent";
import { MessageTextContent } from "../../../components/Message/TextContent";
import { formatPhoneByDDI } from "../../../helpers/phoneHelper";
import { canCopyMessage, canForwardMessage, canRetryPersistedMessage, copyMessageToClipboard } from "../../../utils/messageActions";
import { useSnackbar } from "notistack";
import { ForwardMessageModal } from "../../../components/Modals/ForwardMessageModal";
import { getAssignedRole, hasRole, roleGroups } from "../../../services/accessControl";

type MessageActionSide = "left" | "right";
type MessageReactionSide = "received" | "sent";
type MultiMessageSelectionMode = "delete" | "forward";

interface MessageActionMenuPosition {
  left: number;
  top: number;
}

const reactionEmojiOptions = [ "👍", "❤️", "😂", "😮", "😢", "🙏" ];
const sendEmojiOptions = [
  "😀", "😃", "😄", "😁", "😊", "😉", "😍", "😘",
  "😎", "🤔", "😅", "😂", "🤣", "🥰", "😇", "🙂",
  "🙌", "👏", "👍", "👎", "🙏", "🤝", "💪", "👀",
  "❤️", "💙", "💚", "💛", "🔥", "✨", "🎉", "✅",
];
const messageActionBoundaryPadding = 8;
const AnimatedDotsThreeVertical = motion.create( DotsThreeVertical );
const AnimatedPaperclip = motion.create( Paperclip );
const AnimatedSmiley = motion.create( Smiley );
const AnimatedSticker = motion.create( StickerIcon );
const AnimatedNotePencil = motion.create( NotePencil );
const AnimatedX = motion.create( X );
const menuToggleIconStyle = { position: "absolute" as const };
const menuToggleIconTransition = { duration: .2 };
const serviceSkeletonItems = [ 0, 1, 2, 3, 4, 5 ];
const messageSkeletonItems = [
  { side: "left", width: 54, lines: [ 62, 88 ] },
  { side: "right", width: 42, lines: [ 76 ] },
  { side: "left", width: 48, lines: [ 56, 82, 44 ] },
  { side: "right", width: 58, lines: [ 92, 64 ] },
] as const;

const canDeleteMessage = ( message: Message ): boolean => (
  !message.deletedAt &&
  !message.channelDeletedAt &&
  message.ownerType === OwnerTypeMessage.USER &&
  Boolean( message.providerMessageId )
);

const serviceStatusLabels: Record<ServiceStatus, string> = {
  [ ServiceStatus.AUTOMATED ]: "Bot",
  [ ServiceStatus.WAITING ]: "Aguardando",
  [ ServiceStatus.PROGRESS ]: "Em atendimento",
  [ ServiceStatus.FINALIZED ]: "Finalizado",
  [ ServiceStatus.CONVERSATIONS ]: "Conversa",
};

interface ServiceSearchOccurrenceItem {
  service: Service;
  occurrence?: Message;
}

const escapeRegExp = ( value: string ) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const resolveMessageActionMenuPlacement = (
  actionsElement: HTMLElement | null,
  menuElement: HTMLDivElement | null,
  scrollElement: HTMLDivElement | null,
  bottomBoundaryElement: HTMLDivElement | null,
  side: MessageActionSide,
): MessageActionMenuPosition | null => {
  if( !actionsElement || !menuElement || !scrollElement ) return null;

  const actionsRect = actionsElement.getBoundingClientRect();
  const menuRect = menuElement.getBoundingClientRect();
  const scrollRect = scrollElement.getBoundingClientRect();
  const bottomBoundaryRect = bottomBoundaryElement?.getBoundingClientRect();
  const topLimit = scrollRect.top + messageActionBoundaryPadding;
  const bottomLimit = Math.min( scrollRect.bottom, bottomBoundaryRect?.top ?? scrollRect.bottom ) - messageActionBoundaryPadding;
  const leftLimit = scrollRect.left + messageActionBoundaryPadding;
  const rightLimit = scrollRect.right - messageActionBoundaryPadding;
  const preferredLeft = side === "left"
    ? actionsRect.right + messageActionBoundaryPadding
    : actionsRect.left - menuRect.width - messageActionBoundaryPadding;
  const preferredTop = actionsRect.top;
  const maxTop = Math.max( topLimit, bottomLimit - menuRect.height );
  const maxLeft = Math.max( leftLimit, rightLimit - menuRect.width );

  return {
    left: Math.min( Math.max( preferredLeft, leftLimit ), maxLeft ),
    top: Math.min( Math.max( preferredTop, topLimit ), maxTop ),
  };
};

interface MessageBubbleActionsProps {
  message: Message;
  side: MessageActionSide;
  menuOpen: boolean;
  reactionOpen: boolean;
  hidden: boolean;
  iconSize: number;
  scrollContainerRef: RefObject<HTMLDivElement>;
  bottomBoundaryRef: RefObject<HTMLDivElement>;
  onToggleMenu: ( messageId: string ) => void;
  onCloseMenu: () => void;
  onToggleReaction: ( messageId: string ) => void;
  onCloseReaction: () => void;
  onReply: ( message: Message ) => void;
  onReact: ( message: Message ) => void;
  onSelectReaction: ( message: Message, emoji: string ) => void;
  onEdit: ( message: Message ) => void;
  onDelete: ( message: Message ) => void;
  onSaveSticker: ( message: Message ) => void;
  onCopy: ( message: Message ) => void;
  onForward: ( message: Message ) => void;
  onStartDeleteSelection: ( message: Message ) => void;
  onStartForwardSelection: ( message: Message ) => void;
}

const MessageBubbleActions = ({
  message,
  side,
  menuOpen,
  reactionOpen,
  hidden,
  iconSize,
  scrollContainerRef,
  bottomBoundaryRef,
  onToggleMenu,
  onCloseMenu,
  onToggleReaction,
  onCloseReaction,
  onReply,
  onReact,
  onSelectReaction,
  onEdit,
  onDelete,
  onSaveSticker,
  onCopy,
  onForward,
  onStartDeleteSelection,
  onStartForwardSelection,
}: MessageBubbleActionsProps ) => {
  const actionsRef = useRef<HTMLDivElement | null>( null );
  const reactionTriggerRef = useRef<HTMLButtonElement | null>( null );
  const menuTriggerRef = useRef<HTMLButtonElement | null>( null );
  const menuRef = useRef<HTMLDivElement | null>( null );
  const reactionRef = useRef<HTMLDivElement | null>( null );
  const [ menuPosition, setMenuPosition ] = useState<MessageActionMenuPosition | null>( null );
  const [ reactionPosition, setReactionPosition ] = useState<MessageActionMenuPosition | null>( null );
  const canMutateSentMessage = canDeleteMessage( message );
  const canEditMessage = canMutateSentMessage && message.messageType === MessageType.TEXT;
  const actionsOpen = menuOpen || reactionOpen;

  useLayoutEffect(() => {
    if( !menuOpen ) {
      setMenuPosition( null );
      return;
    }

    setMenuPosition( resolveMessageActionMenuPlacement( menuTriggerRef.current, menuRef.current, scrollContainerRef.current, bottomBoundaryRef.current, side ));
  }, [ menuOpen, scrollContainerRef, bottomBoundaryRef, side ]);

  useLayoutEffect(() => {
    if( !reactionOpen ) {
      setReactionPosition( null );
      return;
    }

    setReactionPosition( resolveMessageActionMenuPlacement( reactionTriggerRef.current, reactionRef.current, scrollContainerRef.current, bottomBoundaryRef.current, side ));
  }, [ reactionOpen, scrollContainerRef, bottomBoundaryRef, side ]);

  useEffect(() => {
    if( !menuOpen && !reactionOpen ) return;

    const updatePlacement = () => {
      if( menuOpen ) {
        setMenuPosition( resolveMessageActionMenuPlacement( menuTriggerRef.current, menuRef.current, scrollContainerRef.current, bottomBoundaryRef.current, side ));
      }
      if( reactionOpen ) {
        setReactionPosition( resolveMessageActionMenuPlacement( reactionTriggerRef.current, reactionRef.current, scrollContainerRef.current, bottomBoundaryRef.current, side ));
      }
    };

    const scrollElement = scrollContainerRef.current;
    window.addEventListener( "resize", updatePlacement );
    scrollElement?.addEventListener( "scroll", updatePlacement, { passive: true });

    return () => {
      window.removeEventListener( "resize", updatePlacement );
      scrollElement?.removeEventListener( "scroll", updatePlacement );
    };
  }, [ menuOpen, reactionOpen, scrollContainerRef, bottomBoundaryRef, side ]);

  useEffect(() => {
    if( !menuOpen && !reactionOpen ) return;

    const handleDocumentMouseDown = ( event: globalThis.MouseEvent ) => {
      const target = event.target as Node;
      if( actionsRef.current?.contains( target ) || menuRef.current?.contains( target ) || reactionRef.current?.contains( target )) return;
      if( menuOpen ) onCloseMenu();
      if( reactionOpen ) onCloseReaction();
    };

    document.addEventListener( "mousedown", handleDocumentMouseDown );
    return () => document.removeEventListener( "mousedown", handleDocumentMouseDown );
  }, [ menuOpen, reactionOpen, onCloseMenu, onCloseReaction ]);

  if( message.channelDeletedAt ) return <></>;

  const menu = menuOpen && typeof document !== "undefined"
    ? createPortal(
        <MessageActionMenu
          ref={ menuRef }
          $side={ side }
          $open={ menuOpen }
          $floatingLeft={ menuPosition?.left }
          $floatingTop={ menuPosition?.top }
          onMouseDown={( event ) => event.stopPropagation()}
          onClick={( event ) => event.stopPropagation()}
        >
          <MessageActionButton
            $color="text"
            onClick={() => onReply( message )}
          >
            <ArrowBendUpLeft size={ iconSize } />
            <span>Responder</span>
          </MessageActionButton>

          {
            canCopyMessage( message ) && (
              <MessageActionButton $color="text" onClick={() => onCopy( message )}>
                <Copy size={ iconSize } />
                <span>Copiar</span>
              </MessageActionButton>
            )
          }

          {
            canForwardMessage( message ) && (
              <>
                <MessageActionButton $color="text" onClick={() => onForward( message )}>
                  <ShareFat size={ iconSize } />
                  <span>Encaminhar</span>
                </MessageActionButton>

                <MessageActionButton $color="text" onClick={() => onStartForwardSelection( message )}>
                  <Check size={ iconSize } />
                  <span>Selecionar para encaminhar</span>
                </MessageActionButton>
              </>
            )
          }

          <MessageActionButton
            $color="text"
            onClick={() => onReact( message )}
          >
            <Smiley size={ iconSize } />
            <span>Reagir</span>
          </MessageActionButton>

          {
            message.ownerType === OwnerTypeMessage.CONTACT && message.messageType === MessageType.STICKER && message.fileMetadata
              ? (
                  <MessageActionButton
                    $color="text"
                    onClick={() => onSaveSticker( message )}
                  >
                    <StickerIcon size={ iconSize } />
                    <span>Salvar figurinha</span>
                  </MessageActionButton>
                )
              : <></>
          }

          {
            canEditMessage
              ? (
                  <MessageActionButton
                    $color="text"
                    onClick={() => onEdit( message )}
                  >
                    <PencilSimple size={ iconSize } />
                    <span>Editar</span>
                  </MessageActionButton>
                )
              : <></>
          }

          {
            canMutateSentMessage
              ? (
                  <>
                    <MessageActionButton
                      $color="text"
                      onClick={() => onStartDeleteSelection( message )}
                    >
                      <Check size={ iconSize } />
                      <span>Selecionar para excluir</span>
                    </MessageActionButton>

                    <MessageActionButton
                      $color="error"
                      onClick={() => onDelete( message )}
                    >
                      <Trash size={ iconSize } />
                      <span>Excluir</span>
                    </MessageActionButton>
                  </>
                )
              : <></>
          }
        </MessageActionMenu>,
        document.body,
      )
    : <></>;

  const reactionPicker = reactionOpen && typeof document !== "undefined"
    ? createPortal(
        <MessageReactionPicker
          ref={ reactionRef }
          $side={ side }
          $open={ reactionOpen }
          $floatingLeft={ reactionPosition?.left }
          $floatingTop={ reactionPosition?.top }
          onMouseDown={( event ) => event.stopPropagation()}
          onClick={( event ) => event.stopPropagation()}
          onDoubleClick={( event ) => event.stopPropagation()}
        >
          {
            reactionEmojiOptions.map(( emoji ) => (
              <button
                key={ emoji }
                type="button"
                aria-label={ `Reagir com ${ emoji }` }
                onClick={() => onSelectReaction( message, emoji )}
              >
                { emoji }
              </button>
            ))
          }
        </MessageReactionPicker>,
        document.body,
      )
    : <></>;

  return (
    <>
      <MessageActions
        ref={ actionsRef }
        className={ `message-actions${ hidden ? " message-actions-hidden" : "" }` }
        $side={ side }
        $open={ actionsOpen }
        onDoubleClick={( event ) => event.stopPropagation()}
      >
        <button
          ref={ reactionTriggerRef }
          type="button"
          className="message-action-trigger reaction-action-trigger"
          aria-label="Reagir à mensagem"
          aria-expanded={ reactionOpen }
          onClick={( event ) => {
            event.stopPropagation();
            onToggleReaction( message.id );
          }}
        >
          <Smiley
            size={ iconSize }
            weight="bold"
          />
        </button>

        <button
          ref={ menuTriggerRef }
          type="button"
          className="message-action-trigger menu-action-trigger"
          aria-label="Opcoes da mensagem"
          aria-expanded={ menuOpen }
          onClick={( event ) => {
            event.stopPropagation();
            onToggleMenu( message.id );
          }}
        >
          <DotsThreeVertical
            size={ iconSize }
            weight="bold"
          />
        </button>
      </MessageActions>

      { menu }
      { reactionPicker }
    </>
  );
};

const ServiceListSkeleton = ({ compact = false }: { compact?: boolean }) => (
  <>
    { serviceSkeletonItems.slice( 0, compact ? 2 : serviceSkeletonItems.length ).map(( item ) => (
      <ServiceSkeletonItem key={ item }>
        <div className="avatar" />

        <div className="content">
          <SkeletonLine $width={ item % 2 === 0 ? 52 : 68 } />
          <SkeletonLine $width={ item % 2 === 0 ? 86 : 74 } />
          <SkeletonLine $width={ item % 2 === 0 ? 44 : 58 } />
        </div>
      </ServiceSkeletonItem>
    ))}
  </>
);

const MessagesSkeleton = ({ compact = false }: { compact?: boolean }) => (
  <SkeletonMessagesView>
    { messageSkeletonItems.slice( 0, compact ? 2 : messageSkeletonItems.length ).map(( item, index ) => (
      <SkeletonMessageBubble
        key={ index }
        $side={ item.side }
        $width={ item.width }
      >
        { item.lines.map(( width, lineIndex ) => (
          <SkeletonLine
            key={ lineIndex }
            $width={ width }
          />
        ))}

        <SkeletonLine
          $width={ 28 }
          className="footer"
        />
      </SkeletonMessageBubble>
    ))}
  </SkeletonMessagesView>
);

export const Chat = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [ searchParams, setSearchParams ] = useSearchParams();
  const routeMessageId = searchParams.get( "messageId" );

  const {
    theme,
    user,
    upsertSavedSticker,
    removeSavedSticker,
  } = useStore();

  const [ sharedContactDraft, setSharedContactDraft ] = useState<SharedContactValue | null>( null );
  const [ openActionMessageId, setOpenActionMessageId ] = useState<string | null>( null );
  const [ hiddenActionMessageId, setHiddenActionMessageId ] = useState<string | null>( null );
  const [ reactionPickerMessageId, setReactionPickerMessageId ] = useState<string | null>( null );
  const [ selectedFeedbackMessageId, setSelectedFeedbackMessageId ] = useState<string | null>( null );
  const [ deleteTargetMessage, setDeleteTargetMessage ] = useState<Message | null>( null );
  const [ forwardTargetMessages, setForwardTargetMessages ] = useState<Array<Message>>([]);
  const [ multiSelectionMode, setMultiSelectionMode ] = useState<MultiMessageSelectionMode | null>( null );
  const [ selectedMessages, setSelectedMessages ] = useState<Map<string, Message>>( new Map());
  const [ deletingSelectedMessages, setDeletingSelectedMessages ] = useState<boolean>( false );
  const [ showEmojiPicker, setShowEmojiPicker ] = useState<boolean>( false );
  const [ showStickerPicker, setShowStickerPicker ] = useState<boolean>( false );
  const [ desktopDetails, setDesktopDetails ] = useState<boolean>(() => typeof window !== "undefined" && window.matchMedia("(min-width: 1440px)").matches );
  const [ detailsOpen, setDetailsOpen ] = useState<boolean>(() => typeof window !== "undefined" && window.matchMedia("(min-width: 1440px)").matches );
  const [ selectedAttachmentPreviewIndex, setSelectedAttachmentPreviewIndex ] = useState<number>( 0 );
  const assignedRole = getAssignedRole( user );
  const savedStickers = user?.savedStickers ?? [];
  const selectedMessagesList = useMemo(() => Array.from( selectedMessages.values()), [ selectedMessages ]);
  const selectedMessagesCount = selectedMessages.size;
  const chatBottomBarRef = useRef<HTMLDivElement | null>( null );
  const contextPanelRef = useRef<HTMLElement | null>( null );
  const serviceMenuTriggerRef = useRef<HTMLButtonElement | null>( null );
  const serviceMenuRef = useRef<HTMLDivElement | null>( null );
  const selectedFeedbackTimeoutRef = useRef<number | null>( null );
  const replyPreviewScrollCleanupRef = useRef<(() => void) | null>( null );
  const pendingSearchOccurrenceRef = useRef<string | null>( null );

  const {
    showMenuFilter,
    showScrollToBottom,
    search,
    queue,
    channel,
    status,
    queues,
    channels,
    services,
    groupedMessages,
    scrollServicesRef,
    scrollMessagesRef,
    loadingServices,
    loadingMessages,
    loadingMessagesPage,
    selectedServiceChat,
    editor,
    message,
    internalMessage,
    replyTargetMessage,
    editTargetMessage,
    attachments,
    channelIsConnected,
    order,
    totalWaiting,
    showServiceMenu,
    showAttachmentMenu,
    showTransferModalConfirm,
    showAbandonModalConfirm,
    showCloseModalConfirm,
    showTransferServiceModal,
    showStartConversationModal,
    currentAudio,
    audioRate,
    attachmentType,
    attachmentInputRef,
    handleChangeAttachment,
    handleRemoveAttachment,
    handlePlayAudio,
    handleStopAudio,
    handleNextAudioRate,
    handleToggleShowServiceMenu,
    handleToggleShowAttachmentMenu,
    handleToggleShowTransferModalConfirm,
    handleToggleShowAbandonModalConfirm,
    handleToggleShowCloseModalConfirm,
    handleToggleShowTransferServiceModal,
    handleToggleShowStartConversationModal,
    handleToggleInternalMessage,
    handleSelectServiceChat,
    handleCloseServiceChat,
    setSearch,
    setQueue,
    setChannel,
    setStatus,
    setShowMenuFilter,
    cleanFilters,
    onScrollServicesCapture,
    onScrollMessagesCapture,
    handleScrollToBottom,
    loadMessageContext,
    handleSendMessage,
    handleRetryUploadMessage,
    handleRetryMessage,
    retryingMessageIds,
    handleSendSavedSticker,
    handleReplyToMessage,
    handleClearReplyToMessage,
    clearEditTargetMessage,
    handleEditMessage,
    handleDeleteMessage,
    handleReactToMessage,
    handleToggleOrder,
    handleTransferService,
    handleStartSelectedConversation,
    handleAbandonService,
    handleCloseService,
    handleStartConversationFromSharedContact,
    changeAttachmentType,
  } = useChatController();

  const handleServiceMenuButtonClick = () => handleToggleShowServiceMenu( !showServiceMenu );

  useEffect(() => {
    const desktopMedia = window.matchMedia("(min-width: 1440px)");
    const handleDesktopChange = ({ matches }: MediaQueryListEvent ) => {
      setDesktopDetails( matches );
      setDetailsOpen( matches );
    };

    desktopMedia.addEventListener( "change", handleDesktopChange );
    return () => desktopMedia.removeEventListener( "change", handleDesktopChange );
  }, []);

  useEffect(() => {
    if( !detailsOpen || desktopDetails || !contextPanelRef.current ) return;

    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const panel = contextPanelRef.current;
    const focusable = Array.from( panel.querySelectorAll<HTMLElement>(
      "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])"
    ));
    const firstFocusable = focusable[ 0 ];
    const lastFocusable = focusable[ focusable.length - 1 ];

    firstFocusable?.focus();

    const handleKeyDown = ( event: KeyboardEvent ) => {
      if( event.key === "Escape" ) {
        event.preventDefault();
        setDetailsOpen( false );
        return;
      }

      if( event.key !== "Tab" || !firstFocusable || !lastFocusable ) return;

      if( event.shiftKey && document.activeElement === firstFocusable ) {
        event.preventDefault();
        lastFocusable.focus();
      } else if( !event.shiftKey && document.activeElement === lastFocusable ) {
        event.preventDefault();
        firstFocusable.focus();
      }
    };

    document.addEventListener( "keydown", handleKeyDown );

    return () => {
      document.removeEventListener( "keydown", handleKeyDown );
      previousFocus?.focus();
    };
  }, [ detailsOpen, desktopDetails ]);

  useEffect(() => {
    if( !showServiceMenu || !serviceMenuRef.current ) return;
    const trigger = serviceMenuTriggerRef.current;

    requestAnimationFrame(() => serviceMenuRef.current?.querySelector<HTMLButtonElement>( "button" )?.focus());
    const handleKeyDown = ( event: KeyboardEvent ) => {
      if( event.key !== "Escape" ) return;
      event.preventDefault();
      handleToggleShowServiceMenu( false );
    };
    document.addEventListener( "keydown", handleKeyDown );
    return () => {
      document.removeEventListener( "keydown", handleKeyDown );
      trigger?.focus();
    };
    // The controller handler is intentionally excluded to avoid reopening focus on each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ showServiceMenu ]);

  const handleAttachmentMenuButtonClick = () => {
    setShowEmojiPicker( false );
    setShowStickerPicker( false );
    handleToggleShowAttachmentMenu( !showAttachmentMenu );
  };

  const handleEmojiPickerButtonClick = () => {
    handleToggleShowAttachmentMenu( false );
    setShowStickerPicker( false );
    setShowEmojiPicker(( show ) => !show );
  };

  const handleStickerPickerButtonClick = () => {
    handleToggleShowAttachmentMenu( false );
    setShowEmojiPicker( false );
    setShowStickerPicker(( show ) => !show );
  };

  useEffect(() => {
    if( attachments.length === 0 ) return;

    setReactionPickerMessageId( null );
    setOpenActionMessageId( null );
    setHiddenActionMessageId( null );
    setShowEmojiPicker( false );
    setShowStickerPicker( false );
    if( showAttachmentMenu ) handleToggleShowAttachmentMenu( false );
  }, [ attachments, handleToggleShowAttachmentMenu, showAttachmentMenu ]);

  useEffect(() => {
    setSelectedAttachmentPreviewIndex( 0 );
  }, [ attachments ]);

  useEffect(() => {
    setOpenActionMessageId( null );
    setHiddenActionMessageId( null );
    setReactionPickerMessageId( null );
    setDeleteTargetMessage( null );
    setForwardTargetMessages( [] );
    setMultiSelectionMode( null );
    setSelectedMessages( new Map());
    setShowEmojiPicker( false );
    setShowStickerPicker( false );
    if( typeof window !== "undefined" && window.innerWidth < 1440 ) setDetailsOpen( false );
    if( internalMessage ) handleToggleInternalMessage();
    // eslint-disable-next-line
  }, [ selectedServiceChat?.chat.id ]);

  const serviceStatus: Array<{
    label: string;
    value: ServiceStatus;
    badgeCount?: number;
    roles: ReadonlyArray<UserRole>;
  } | "separator"> = [
    {
      value: ServiceStatus.PROGRESS,
      roles: roleGroups.all,
      label: "Atendendo",
    },
    {
      value: ServiceStatus.WAITING,
      roles: roleGroups.all,
      label: "Aguardando",
      badgeCount: totalWaiting,
    },
    {
      value: ServiceStatus.AUTOMATED,
      roles: roleGroups.chat,
      label: "Bot",
    },
    "separator",
    {
      value: ServiceStatus.CONVERSATIONS,
      roles: roleGroups.all,
      label: "Conversas",
    },
  ];

  const serviceMenuItems: Array<{
    label: string;
    onClick: () => void;
    color: Color;
    roles: ReadonlyArray<UserRole>;
    Icon: Icon;
    show: boolean;
  }> = [
    {
      label: "Pesquisar",
      onClick: () => {}, // TODO - Abrir barra de buscar por mensagens...
      color: "text",
      roles: roleGroups.all,
      Icon: MagnifyingGlass,
      show: true,
    },
    {
      label: "Transferir",
      onClick: () => {
        handleToggleShowServiceMenu( false );

        handleToggleShowTransferModalConfirm( true );
      },
      color: "text",
      roles: roleGroups.chat,
      Icon: ShareFat,
      show: !selectedServiceChat?.isDraft,
    },
    {
      label: "Devolver",
      onClick: () => {
        handleToggleShowServiceMenu( false );

        handleToggleShowAbandonModalConfirm( true );
      },
      color: "text",
      roles: roleGroups.all,
      Icon: SortAscending,
      show: selectedServiceChat?.status === ServiceStatus.PROGRESS && !selectedServiceChat?.isDraft,
    },
    {
      label: "Encerrar",
      onClick: () => {
        handleToggleShowServiceMenu( false );

        handleToggleShowCloseModalConfirm( true );
      },
      color: "error",
      roles: roleGroups.all,
      Icon: ChatSlash,
      show: selectedServiceChat?.status === ServiceStatus.PROGRESS && !selectedServiceChat?.isDraft,
    },
  ];

  const attachmentMenuItems: Array<{
    label: string;
    onClick: () => void;
    Icon: Icon;
  }> = [
    {
      label: "Documentos",
      onClick: () => changeAttachmentType("document"),
      Icon: File,
    },
    {
      label: "Imagens e vídeos",
      onClick: () => changeAttachmentType("media"),
      Icon: FileImage,
    },
    {
      label: "Áudios",
      onClick: () => changeAttachmentType("audio"),
      Icon: FileAudio,
    },
  ];

  const showCleanButton =
    search !== "" ||
    queue !== defaultQueue ||
    channel !== defaultChannel ||
    status !== ServiceStatus.PROGRESS;

  const normalizedSearch = search.trim();

  const serviceSearchOccurrenceItems = useMemo<Array<ServiceSearchOccurrenceItem>>(() => {
    if( !normalizedSearch ) return services.map(( service ) => ({ service }));

    return services.flatMap(( service ) => {
      const occurrences = service.chat.searchOccurrences ?? [];
      if( occurrences.length === 0 ) return [{ service }];

      return occurrences.map(( occurrence ) => ({ service, occurrence }));
    });
  }, [ normalizedSearch, services ]);

  const getLastMessageSenderLabel = ( service: Service, message = service.chat.messages?.[ 0 ]) => {

    return resolveLastMessageSenderLabel( message, {
      currentUser: user,
      contactName: service.chat.contact.name,
      organizationName: service.chat.contact.organization?.name,
    });
  };

  const renderHighlightedSearchText = ( value: string, searchValue: string ): ReactNode => {
    const cleanValue = removeFormattingMarksPreservingSpacing( value );
    const cleanSearch = searchValue.trim();
    if( !cleanValue || !cleanSearch ) return cleanValue;

    const splitMatcher = new RegExp(`(${ escapeRegExp( cleanSearch ) })`, "ig");
    const testMatcher = new RegExp(`^${ escapeRegExp( cleanSearch ) }$`, "i");
    const parts = cleanValue.split( splitMatcher );

    return parts.map(( part, index ) => (
      testMatcher.test( part )
        ? <mark className="search-hit" key={`${ part }_${ index }`}>{ part }</mark>
        : <Fragment key={`${ part }_${ index }`}>{ part }</Fragment>
    ));
  };

  const getSearchOccurrenceText = ( message: Message ): string => {
    if( message.content ) return message.content;
    return message.fileMetadata?.originalFileName || message.fileMetadata?.fileName || String( getSpecialMessagePreview( message ) || "Ocorrência encontrada" );
  };

  const renderSearchOccurrencePreview = ( message: Message ): ReactNode => (
    <>
      <MagnifyingGlass
        size={ 14 }
        className="content-message-icon"
        weight="bold"
      />

      <span className="content-message occurrence-message">
        { renderHighlightedSearchText( getSearchOccurrenceText( message ), normalizedSearch )}
      </span>
    </>
  );

  const getUserNameByMessage = ( message: Message ) => {
    return resolveMessageSenderName( message, {
      currentUser: user,
      contactName: selectedServiceChat?.chat.contact.name,
    });
  };

  const getProfile = ( message: Message ) => {
    return buildInlineFileURL( resolveMessageSenderAvatarReference( message ));
  };

  const selectedHistoricalConversation = Boolean( selectedServiceChat?.isDraft && selectedServiceChat.chat?.id && !selectedServiceChat.chat.id.startsWith( "draft-" ));

  const getStatusIcon = ( status: MessageStatus ): ReactNode => {
    const iconStatusMapper: {
      [ key in MessageStatus ]: ReactNode;
    } = {
      SENDING:
        <Clock
          color={ theme.colors.gray[100] }
          weight="bold"
          size={ theme.font_size.xxx_small }
        />,
      SENT:
        <Check
          color={ theme.colors.gray[100] }
          weight="bold"
          size={ theme.font_size.xxx_small }
        />,
      DELIVERED:
        <Checks
          color={ theme.colors.gray[100] }
          weight="bold"
          size={ theme.font_size.xxx_small }
        />,
      READ:
        <Checks
          color={ theme.colors.info.default }
          weight="bold"
          size={ theme.font_size.xxx_small }
        />,
      FAILED:
        <X
          color={ theme.colors.error.default }
          weight="bold"
          size={ theme.font_size.xxx_small }
          aria-label="Falha ao enviar"
        />,
    };

    return iconStatusMapper[ status ];
  };

  const getMessageMutationLabel = ( message: Message ): string => {
    if( message.channelDeletedAt ) return "Mensagem apagada";
    if( message.editedAt ) return "Mensagem editada";
    return "";
  };

  const renderLastMessage = ( message: Message ): ReactNode => {
    if( message.channelDeletedAt ) return "Mensagem apagada";

    const lastMessageMapper: {
      [ key in MessageType ]: ReactNode;
    } = {
      [ MessageType.EMPTY ]: "",
      [ MessageType.LOADING ]: (
        <></>
      ),
      [ MessageType.TEXT ]: removeFormattingMarksPreservingSpacing( message.content ),
      [ MessageType.AUDIO ]: (
        <>
          <Microphone
            size={ 18 }
            className="content-message-icon"
            weight="duotone"
          />

          <span className="content-message">{ formatDuration(( message.fileMetadata?.duration ?? 0 ) * 1000 )}</span>
        </>
      ),
      [ MessageType.IMAGE ]: (
        <>
          <ImageIcon
            size={ 18 }
            className="content-message-icon"
            weight="duotone"
          />

          <span className="content-message">{ message.content ? removeFormattingMarksPreservingSpacing( message.content ) : "Imagem"}</span>
        </>
      ),
      [ MessageType.STICKER ]: (
        <>
          <FileImage
            size={ 18 }
            className="content-message-icon"
            weight="duotone"
          />

          <span className="content-message">Figurinha</span>
        </>
      ),
      [ MessageType.DOCUMENT ]: (
        <>
          <File
            size={ 18 }
            className="content-message-icon"
            weight="duotone"
          />

          <span className="content-message">{ message.content ? removeFormattingMarksPreservingSpacing( message.content ) : message.fileMetadata?.fileName ?? "Documento"}</span>
        </>
      ),
      [ MessageType.VIDEO ]: (
        <>
          <VideoCamera
            size={ 18 }
            className="content-message-icon"
            weight="duotone"
          />

          <span className="content-message">{ message.content ? removeFormattingMarksPreservingSpacing( message.content ) : "Vídeo"}</span>
        </>
      ),
      [ MessageType.LOCATION ]: (
        <>
          <MapPin
            size={ 18 }
            className="content-message-icon"
            weight="duotone"
          />

          <span className="content-message">{ getSpecialMessagePreview( message )}</span>
        </>
      ),
      [ MessageType.LIVE_LOCATION ]: (
        <>
          <MapPin
            size={ 18 }
            className="content-message-icon"
            weight="duotone"
          />

          <span className="content-message">{ getSpecialMessagePreview( message )}</span>
        </>
      ),
      [ MessageType.CONTACT ]: (
        <>
          <AddressBook
            size={ 18 }
            className="content-message-icon"
            weight="duotone"
          />

          <span className="content-message">{ getSpecialMessagePreview( message )}</span>
        </>
      ),
      [ MessageType.REACTION ]: "",
      [ MessageType.POLL ]: (
        <>
          <Checks
            size={ 18 }
            className="content-message-icon"
            weight="duotone"
          />

          <span className="content-message">{ getSpecialMessagePreview( message )}</span>
        </>
      ),
      [ MessageType.INTERNAL ]: (
        <>
          <NotePencil
            size={ 18 }
            className="content-message-icon"
            weight="duotone"
          />

          <span className="content-message">Comentário interno: { removeFormattingMarksPreservingSpacing( message.content )}</span>
        </>
      ),
    };

    return lastMessageMapper[ message.messageType ];
  };

  const renderMessageContent = ( message: Message ): ReactNode => {
    if( message.messageType === MessageType.LOADING ) {
      const upload = message.localUpload;
      const percentage = upload?.percentage ?? 0;

      return (
        <div className={ `upload-feedback ${ upload?.status === "FAILED" ? "failed" : "" }` }>
          <strong>{ upload?.status === "FAILED" ? "Falha ao enviar arquivo" : "Enviando arquivo" }</strong>
          <span>{ upload?.fileName ?? "Arquivo selecionado" }</span>

          <div className="upload-progress" aria-label={ `Progresso ${ percentage }%` }>
            <span style={{ width: `${ percentage }%` }} />
          </div>

          <small>{ upload?.status === "FAILED" ? upload.error ?? "Nao foi possivel enviar o arquivo." : `${ percentage }% concluido` }</small>

          {
            upload?.status === "FAILED" && upload.retryable
              ? (
                  <button
                    type="button"
                    onClick={() => handleRetryUploadMessage( message.id )}
                  >
                    Tentar novamente
                  </button>
                )
              : <></>
          }
        </div>
      );
    }

    const messageRenderMapper: {
      [ key in string ]: ReactNode;
    } = {
      audio: (
        <Audio
          data={ message.fileMetadata as FileMetadata }
          currentAudio={ currentAudio }
          audioRate={ audioRate }
          handlePlayAudio={ handlePlayAudio }
          handleStopAudio={ handleStopAudio }
          handleNextAudioRate={ handleNextAudioRate }
        />
      ),
      image: (
        <Image
          message={ message }
          user={ user as User }
          contact={ selectedServiceChat?.chat.contact as Contact }
        />
      ),
      sticker: (
        <Sticker
          message={ message }
        />
      ),
      location: (
        <Location
          message={ message }
        />
      ),
      live_location: (
        <Location
          message={ message }
        />
      ),
      contact: (
        <SharedContact
          message={ message }
          onSaveContact={ setSharedContactDraft }
          onStartConversation={ handleStartConversationFromSharedContact }
        />
      ),
      document: (
        <Document
          message={ message }
          user={ user as User }
          contact={ selectedServiceChat?.chat.contact as Contact }
        />
      ),
      video: (
        <Video
          message={ message }
          user={ user as User }
          contact={ selectedServiceChat?.chat.contact as Contact }
        />
      ),
      poll: <Poll message={ message } />,
    };

    if( message.messageType === MessageType.TEXT ) {
      return <MessageTextContent content={ message.content } />;
    } else if( message.messageType === MessageType.REACTION ) {
      return <></>;
    } else {
      return messageRenderMapper[ message.messageType.toLocaleLowerCase()];
    }
  };

  const renderMessage = ( message: Message ): ReactNode => {
    if( message.channelDeletedAt && !canSeeDeletedContent( assignedRole )) return <></>;
    return renderMessageContent( message );
  };

  useEffect(() => () => {
    if( selectedFeedbackTimeoutRef.current !== null ) {
      window.clearTimeout( selectedFeedbackTimeoutRef.current );
    }
    replyPreviewScrollCleanupRef.current?.();
  }, []);

  const getMessagePreview = ( message?: Message | null ): string => {
    if( !message ) return "Mensagem";
    if( message.channelDeletedAt ) return "Mensagem apagada";
    if( message.messageType === MessageType.INTERNAL ) return removeFormattingMarksPreservingSpacing( message.content ?? "" ) || "Comentário interno";
    if( message.messageType === MessageType.TEXT ) return removeFormattingMarksPreservingSpacing( message.content ?? "" ) || "Mensagem";
    if( message.messageType === MessageType.LOCATION || message.messageType === MessageType.LIVE_LOCATION || message.messageType === MessageType.CONTACT || message.messageType === MessageType.POLL ) return getSpecialMessagePreview( message );
    if( message.content ) return removeFormattingMarksPreservingSpacing( message.content );
    if( message.messageType === MessageType.AUDIO ) return message.fileMetadata?.duration ? formatDuration( message.fileMetadata.duration * 1000 ) : "Áudio";
    if( message.messageType === MessageType.IMAGE ) return "Imagem";
    if( message.messageType === MessageType.STICKER ) return "Figurinha";
    if( message.messageType === MessageType.VIDEO ) return "Vídeo";
    if( message.messageType === MessageType.DOCUMENT ) return message.fileMetadata?.fileName || "Documento";
    return message.fileMetadata?.fileName || message.messageType;
  };

  const getReplyAuthorName = ( message: Message ): string => {
    return resolveMessageSenderName( message, {
      currentUser: user,
      contactName: selectedServiceChat?.chat.contact.name,
      emptyLabel: "Mensagem",
    });
  };

  const getReplyPreviewImageURL = ( message: Message ): string => {
    const fileMetadata = message.fileMetadata;
    const thumbnailReference = fileMetadata?.thumbnailKey || fileMetadata?.thumbnailUrl;
    if( thumbnailReference ) return buildInlineFileURL( thumbnailReference );

    if( ![ MessageType.IMAGE, MessageType.VIDEO, MessageType.STICKER ].includes( message.messageType )) return "";

    const reference = fileMetadata?.fileId || fileMetadata?.storageKey || fileMetadata?.url;
    return buildInlineFileURL( reference );
  };

  const renderReplyPreviewMedia = ( message: Message ): ReactNode => {
    const imageURL = getReplyPreviewImageURL( message );
    if( imageURL ) {
      return (
        <ReplyPreviewMedia>
          <img
            src={ imageURL }
            alt={ getMessagePreview( message )}
          />
        </ReplyPreviewMedia>
      );
    }

    const iconSize = theme.font_size.xxx_medium;
    const iconMapper: Partial<Record<MessageType, ReactNode>> = {
      [ MessageType.AUDIO ]: <Microphone size={ iconSize } weight="duotone" />,
      [ MessageType.DOCUMENT ]: <File size={ iconSize } weight="duotone" />,
      [ MessageType.IMAGE ]: <FileImage size={ iconSize } weight="duotone" />,
      [ MessageType.STICKER ]: <FileImage size={ iconSize } weight="duotone" />,
      [ MessageType.VIDEO ]: <FileVideo size={ iconSize } weight="duotone" />,
      [ MessageType.LOCATION ]: <MapPin size={ iconSize } weight="duotone" />,
      [ MessageType.LIVE_LOCATION ]: <MapPin size={ iconSize } weight="duotone" />,
      [ MessageType.CONTACT ]: <AddressBook size={ iconSize } weight="duotone" />,
      [ MessageType.POLL ]: <Checks size={ iconSize } weight="duotone" />,
    };

    const icon = iconMapper[ message.messageType ];
    if( !icon ) return <></>;

    return (
      <ReplyPreviewMedia>
        { icon }
      </ReplyPreviewMedia>
    );
  };

  const activateMessageFeedback = ( messageId: string ) => {
    if( selectedFeedbackTimeoutRef.current !== null ) {
      window.clearTimeout( selectedFeedbackTimeoutRef.current );
    }

    setSelectedFeedbackMessageId( messageId );

    selectedFeedbackTimeoutRef.current = window.setTimeout(() => {
      setSelectedFeedbackMessageId( null );
      selectedFeedbackTimeoutRef.current = null;
    }, 520 );
  };

  const scrollToMessageAndActivateFeedback = ( messageId: string ): boolean => {
    const scrollElement = scrollMessagesRef.current;
    const target = scrollElement?.querySelector(`[data-message-id="${ messageId }"]`);
    if( !scrollElement || !target ) return false;

    let animationFrame: number | null = null;
    let lastScrollTop = scrollElement.scrollTop;
    let stableFrames = 0;
    const timeoutAt = Date.now() + 3000;

    const targetIsVisible = () => {
      const scrollRect = scrollElement.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      return targetRect.bottom >= scrollRect.top && targetRect.top <= scrollRect.bottom;
    };

    const cleanup = () => {
      if( animationFrame !== null ) window.cancelAnimationFrame( animationFrame );
      if( replyPreviewScrollCleanupRef.current === cleanup ) replyPreviewScrollCleanupRef.current = null;
    };

    const finish = () => {
      cleanup();
      activateMessageFeedback( messageId );
    };

    const waitForScrollEnd = () => {
      const currentScrollTop = scrollElement.scrollTop;
      if( Math.abs( currentScrollTop - lastScrollTop ) < 1 ) stableFrames += 1;
      else stableFrames = 0;

      lastScrollTop = currentScrollTop;

      if( targetIsVisible() && stableFrames >= 3 ) {
        finish();
        return;
      }

      if( Date.now() >= timeoutAt ) {
        if( targetIsVisible()) finish();
        else cleanup();
        return;
      }

      animationFrame = window.requestAnimationFrame( waitForScrollEnd );
    };

    replyPreviewScrollCleanupRef.current?.();
    replyPreviewScrollCleanupRef.current = cleanup;

    target.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });

    animationFrame = window.requestAnimationFrame( waitForScrollEnd );
    return true;
  };

  const handleReplyPreviewClick = async ( message: Message ) => {
    if( scrollToMessageAndActivateFeedback( message.id )) return;

    const loaded = await loadMessageContext( message.id );
    if( !loaded ) return;

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        scrollToMessageAndActivateFeedback( message.id );
      });
    });
  };

  const revealSearchOccurrence = async ( messageId: string ): Promise<boolean> => {
    if( scrollToMessageAndActivateFeedback( messageId )) return true;

    const loaded = await loadMessageContext( messageId );
    if( !loaded ) return false;

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        scrollToMessageAndActivateFeedback( messageId );
      });
    });

    return true;
  };

  const handleSelectSearchOccurrence = ( service: Service, occurrence?: Message ) => {
    if( !occurrence ) {
      pendingSearchOccurrenceRef.current = null;
      handleSelectServiceChat( service );
      return;
    }

    pendingSearchOccurrenceRef.current = occurrence.id;

    if( selectedServiceChat?.chat.id === service.chat.id ) {
      void revealSearchOccurrence( occurrence.id ).then(( revealed ) => {
        if( revealed && pendingSearchOccurrenceRef.current === occurrence.id ) pendingSearchOccurrenceRef.current = null;
      });
      return;
    }

    handleSelectServiceChat( service );
  };

  useEffect(() => {
    const messageId = pendingSearchOccurrenceRef.current;
    if( !messageId || !selectedServiceChat?.chat.id ) return;

    void revealSearchOccurrence( messageId ).then(( revealed ) => {
      if( revealed && pendingSearchOccurrenceRef.current === messageId ) pendingSearchOccurrenceRef.current = null;
    });
    // eslint-disable-next-line
  }, [ selectedServiceChat?.chat.id, groupedMessages ]);

  useEffect(() => {
    if( !routeMessageId || !selectedServiceChat?.chat.id ) return;

    pendingSearchOccurrenceRef.current = routeMessageId;

    void revealSearchOccurrence( routeMessageId ).then(( revealed ) => {
      if( revealed && pendingSearchOccurrenceRef.current === routeMessageId ) pendingSearchOccurrenceRef.current = null;
    });

    const nextSearchParams = new URLSearchParams( searchParams );
    nextSearchParams.delete( "messageId" );
    setSearchParams( nextSearchParams, { replace: true });
    // eslint-disable-next-line
  }, [ routeMessageId, selectedServiceChat?.chat.id ]);

  const renderReplyPreviewCard = ( message: Message, onClear?: () => void, composer = false, onClick?: () => void, clearLabel = "Cancelar resposta", variant: "reply" | "edit" = "reply" ): ReactNode => {
    const actionLabel = variant === "edit" ? "Editando mensagem" : "Respondendo mensagem";

    return (
      <ReplyPreviewCard
        as={ onClick ? "button" : "div" }
        type={ onClick ? "button" : undefined }
        $composer={ composer }
        $clickable={ Boolean( onClick )}
        $variant={ variant }
        aria-label={ onClick ? "Ir para a mensagem respondida" : undefined }
        onClick={ onClick
          ? ( event: MouseEvent<HTMLElement> ) => {
              event.stopPropagation();
              onClick();
            }
          : undefined
        }
      >
        { renderReplyPreviewMedia( message )}

        <ReplyPreviewContent>
          {
            composer
              ? (
                  <span className={ `reply-preview-action ${ variant }` }>
                    {
                      variant === "edit"
                        ? <PencilSimple size={ theme.font_size.x_small } weight="bold" />
                        : <ArrowBendUpLeft size={ theme.font_size.x_small } weight="bold" />
                    }
                    { actionLabel }
                  </span>
                )
              : <></>
          }

          <strong>{ getReplyAuthorName( message )}</strong>
          <span>{ getMessagePreview( message )}</span>
        </ReplyPreviewContent>

        {
          onClear
            ? (
                <ReplyPreviewCloseButton
                  aria-label={ clearLabel }
                  $composer={ composer }
                  onClick={( event ) => {
                    event.stopPropagation();
                    onClear();
                  }}
                >
                  <X size={ theme.font_size.xxx_small } weight="bold" />
                </ReplyPreviewCloseButton>
              )
            : <></>
        }
      </ReplyPreviewCard>
    );
  };

  const getReplyTarget = ( message: Message ): Message | null => {
    if( !message.replyToMessageId ) return null;
    for( const group of groupedMessages ) {
      const found = group.messages.find(( item ) => item.id === message.replyToMessageId );
      if( found ) return found;
    }
    return message.replyToMessage ?? null;
  };

  const renderReplyPreview = ( message: Message ): ReactNode => {
    const target = getReplyTarget( message );
    if( !target ) return <></>;
    return renderReplyPreviewCard( target, undefined, false, () => handleReplyPreviewClick( target ));
  };

  const renderReactions = ( message: Message, side: MessageReactionSide ): ReactNode => {
    if( !message.reactions?.length ) return <></>;
    return <MessageReactions $side={ side }>{ message.reactions.map(( reaction ) => reaction.emoji ).join(" ") }</MessageReactions>;
  };

  const handleSelectReaction = ( message: Message, emoji: string ) => {
    setReactionPickerMessageId( null );
    handleReactToMessage( message, emoji );
  };

  const handleCloseMessageActions = ( hiddenMessageId?: string ) => {
    setOpenActionMessageId( null );

    if( hiddenMessageId ) {
      setHiddenActionMessageId( hiddenMessageId );
    }
  };

  const handleMessageRowLeave = () => {
    setOpenActionMessageId( null );
    setHiddenActionMessageId( null );
  };

  const handleToggleMessageActions = ( messageId: string ) => {
    setHiddenActionMessageId( null );
    setReactionPickerMessageId( null );
    setOpenActionMessageId(( current ) => current === messageId ? null : messageId );
  };

  const handleToggleReactionPicker = ( messageId: string ) => {
    setOpenActionMessageId( null );
    setHiddenActionMessageId( null );
    setReactionPickerMessageId(( current ) => current === messageId ? null : messageId );
  };

  const handleMessageDoubleClick = ( message: Message ) => {
    activateMessageFeedback( message.id );
    handleCloseMessageActions();
    handleReplyToMessage( message );
  };

  const handleReplyAction = ( message: Message ) => {
    activateMessageFeedback( message.id );
    handleCloseMessageActions( message.id );
    handleReplyToMessage( message );
  };

  const handleReactionAction = ( message: Message ) => {
    handleCloseMessageActions( message.id );
    setReactionPickerMessageId( message.id );
  };

  const handleSaveStickerAction = async ( message: Message ) => {
    const result = await saveStickerFromMessage( message );
    if( result.data ) upsertSavedSticker( result.data );
    handleCloseMessageActions( message.id );
  };

  const handleEditAction = ( message: Message ) => {
    activateMessageFeedback( message.id );
    handleCloseMessageActions( message.id );
    handleEditMessage( message );
  };

  const handleDeleteAction = ( message: Message ) => {
    handleCloseMessageActions( message.id );
    setDeleteTargetMessage( message );
  };

  const handleCopyAction = ( message: Message ) => {
    handleCloseMessageActions( message.id );
    void copyMessageToClipboard( message, ( text, variant ) => enqueueSnackbar( text, { variant }));
  };

  const handleForwardAction = ( message: Message ) => {
    handleCloseMessageActions( message.id );
    setForwardTargetMessages( [ message ] );
  };

  const messageCanBeSelectedForMode = ( message: Message, mode: MultiMessageSelectionMode | null = multiSelectionMode ): boolean => {
    if( mode === "delete" ) return canDeleteMessage( message );
    if( mode === "forward" ) return canForwardMessage( message );
    return false;
  };

  const toggleSelectedMessage = ( message: Message ) => {
    if( !multiSelectionMode || !messageCanBeSelectedForMode( message )) return;

    setSelectedMessages(( selected ) => {
      const next = new Map( selected );
      if( next.has( message.id )) {
        next.delete( message.id );
      } else {
        next.set( message.id, message );
      }
      return next;
    });
  };

  const startMultiSelection = ( mode: MultiMessageSelectionMode, message: Message ) => {
    if( !messageCanBeSelectedForMode( message, mode )) return;
    handleCloseMessageActions( message.id );
    setReactionPickerMessageId( null );
    setShowEmojiPicker( false );
    setShowStickerPicker( false );
    setMultiSelectionMode( mode );
    setSelectedMessages( new Map([[ message.id, message ]]) );
  };

  const cancelMultiSelection = () => {
    setMultiSelectionMode( null );
    setSelectedMessages( new Map());
    setDeletingSelectedMessages( false );
  };

  const handleMessageSelectionClick = ( message: Message ) => {
    if( !multiSelectionMode ) return;
    toggleSelectedMessage( message );
  };

  const handleConfirmSelectedDelete = async () => {
    if( multiSelectionMode !== "delete" || selectedMessagesCount === 0 || deletingSelectedMessages ) return;
    setDeletingSelectedMessages( true );
    const messagesToDelete = selectedMessagesList.filter( canDeleteMessage );

    for( const selectedMessage of messagesToDelete ) {
      await handleDeleteMessage( selectedMessage );
    }

    enqueueSnackbar(
      messagesToDelete.length === 1 ? "Mensagem excluída" : `${ messagesToDelete.length } mensagens excluídas`,
      { variant: "success" },
    );
    cancelMultiSelection();
  };

  const handleConfirmSelectedForward = () => {
    if( multiSelectionMode !== "forward" || selectedMessagesCount === 0 ) return;
    const messagesToForward = selectedMessagesList.filter( canForwardMessage );
    if( messagesToForward.length === 0 ) return;
    setForwardTargetMessages( messagesToForward );
  };

  const handleConfirmDeleteMessage = async () => {
    if( !deleteTargetMessage ) return;
    await handleDeleteMessage( deleteTargetMessage );
    setDeleteTargetMessage( null );
  };

  const handleSendSticker = ( sticker: SavedSticker ) => {
    setShowStickerPicker( false );
    handleSendSavedSticker( sticker.fileMetadata );
  };

  const handleInsertEmoji = ( emoji: string ) => {
    editor?.chain().focus().insertContent( emoji ).run();
  };

  const handleRemoveSticker = async ( sticker: SavedSticker ) => {
    const result = await removeSavedStickerRequest( sticker.id );
    if( result.data !== false ) removeSavedSticker( sticker.id );
  };

  const renderMessageActions = ( message: Message, side: MessageActionSide ): ReactNode => {
    if( multiSelectionMode ) return <></>;

    const menuOpen = openActionMessageId === message.id;
    const reactionOpen = reactionPickerMessageId === message.id;

    return (
      <MessageBubbleActions
        message={ message }
        side={ side }
        menuOpen={ menuOpen }
        reactionOpen={ reactionOpen }
        hidden={ !menuOpen && !reactionOpen && hiddenActionMessageId === message.id }
        iconSize={ theme.font_size.xxx_medium }
        scrollContainerRef={ scrollMessagesRef }
        bottomBoundaryRef={ chatBottomBarRef }
        onToggleMenu={ handleToggleMessageActions }
        onCloseMenu={ handleCloseMessageActions }
        onToggleReaction={ handleToggleReactionPicker }
        onCloseReaction={() => setReactionPickerMessageId( null )}
        onReply={ handleReplyAction }
        onReact={ handleReactionAction }
        onSelectReaction={ handleSelectReaction }
        onEdit={ handleEditAction }
        onDelete={ handleDeleteAction }
        onSaveSticker={ handleSaveStickerAction }
        onCopy={ handleCopyAction }
        onForward={ handleForwardAction }
        onStartDeleteSelection={( message ) => startMultiSelection( "delete", message )}
        onStartForwardSelection={( message ) => startMultiSelection( "forward", message )}
      />
    );
  };

  const renderMessageFooterMutation = ( message: Message ): ReactNode => {
    const label = getMessageMutationLabel( message );
    return label ? <span className="mutation">{ label }</span> : <></>;
  };

  const renderMultiMessageSelectionBar = (): ReactNode => {
    if( !multiSelectionMode ) return <></>;

    return (
      <MultiMessageSelectionBar role="status">
        <div className="selection-copy">
          <strong>{ multiSelectionMode === "delete" ? "Excluir mensagens" : "Encaminhar mensagens" }</strong>
          <span>{ multiSelectionMode === "delete" ? "Selecione mensagens para excluir." : "Selecione mensagens para encaminhar." }</span>
          <small>{ selectedMessagesCount } selecionada{ selectedMessagesCount === 1 ? "" : "s" }</small>
        </div>

        <div className="selection-actions">
          <Button
            $color="text"
            $variant={ Variant.TEXT }
            $size={ Size.SMALL }
            onClick={ cancelMultiSelection }
          >
            Cancelar
          </Button>

          <Button
            $color={ multiSelectionMode === "delete" ? "error" : "info" }
            $variant={ Variant.CONTAINED }
            $size={ Size.SMALL }
            disabled={ selectedMessagesCount === 0 || deletingSelectedMessages }
            onClick={ multiSelectionMode === "delete" ? () => void handleConfirmSelectedDelete() : handleConfirmSelectedForward }
          >
            { multiSelectionMode === "delete"
              ? deletingSelectedMessages ? "Excluindo..." : "Confirmar exclusão"
              : "Encaminhar" }
          </Button>
        </div>
      </MultiMessageSelectionBar>
    );
  };

  const attachmentPreviewItems = useMemo(() => attachments.map(( attachment ) => ({
    attachment,
    url: URL.createObjectURL( attachment ),
  })), [ attachments ]);

  useEffect(() => () => {
    attachmentPreviewItems.forEach(({ url }) => URL.revokeObjectURL( url ));
  }, [ attachmentPreviewItems ]);

  const previewContent = useMemo(() => {
    const selectedPreview = attachmentPreviewItems[ selectedAttachmentPreviewIndex ];
    const hasMultipleAttachments = attachmentPreviewItems.length > 1;
    if( !selectedPreview ) return <></>;

    const renderAttachmentPreviewContent = ( attachment: File, url: string, thumbnail = false ) => {
      const mimeType = attachment.type.split("/").shift() ?? "";

      if( mimeType === "image" ) {
        return thumbnail
          ? <img src={ url } alt={ attachment.name } />
          : (
              <ImagePreviewContent
                src={ url }
                alt={ attachment.name }
              />
            );
      }

      if( mimeType === "audio" ) {
        return thumbnail
          ? <FileAudio size={ 28 } />
          : <audio controls src={ url } />;
      }

      if( mimeType === "video" ) {
        return (
          <video
            controls={ !thumbnail }
            muted={ thumbnail }
            playsInline
            preload="metadata"
            src={ url }
          />
        );
      }

      if( mimeType === "application" && !thumbnail ) {
        return (
          <iframe
            title={ `Pré-visualização de ${ attachment.name }` }
            src={`${ url }#toolbar=0`}
            width="100%"
            height="100%"
          />
        );
      }

      return <File size={ thumbnail ? 28 : 52 } />;
    };

    const goToPreviousAttachment = () => setSelectedAttachmentPreviewIndex(( current ) => (
      current === 0 ? attachmentPreviewItems.length - 1 : current - 1
    ));
    const goToNextAttachment = () => setSelectedAttachmentPreviewIndex(( current ) => (
      current >= attachmentPreviewItems.length - 1 ? 0 : current + 1
    ));

    return (
      <div className="attachment-gallery">
        <div className="attachment-gallery-main">
          {
            hasMultipleAttachments && (
              <IconButton
                aria-label="Arquivo anterior"
                className="attachment-gallery-arrow attachment-gallery-arrow-left"
                color="text"
                shape={ Shape.CIRCLE }
                onClick={ goToPreviousAttachment }
              >
                <CaretLeft size={ 28 } weight="bold" />
              </IconButton>
            )
          }

          <div className="attachment-gallery-stage">
            { renderAttachmentPreviewContent( selectedPreview.attachment, selectedPreview.url ) }
          </div>

          {
            hasMultipleAttachments && (
              <IconButton
                aria-label="Próximo arquivo"
                className="attachment-gallery-arrow attachment-gallery-arrow-right"
                color="text"
                shape={ Shape.CIRCLE }
                onClick={ goToNextAttachment }
              >
                <CaretRight size={ 28 } weight="bold" />
              </IconButton>
            )
          }
        </div>

        <div className="attachment-gallery-info">
          <strong title={ selectedPreview.attachment.name }>{ selectedPreview.attachment.name }</strong>
          <span>{ selectedAttachmentPreviewIndex + 1 } de { attachmentPreviewItems.length }</span>
        </div>

        {
          hasMultipleAttachments && (
            <div className="attachment-gallery-thumbnails">
              {
                attachmentPreviewItems.map(({ attachment, url }, index ) => (
                  <div
                    className={ index === selectedAttachmentPreviewIndex ? "selected" : undefined }
                    key={ `${ attachment.name }-${ attachment.lastModified }-${ index }` }
                  >
                    <button
                      aria-label={ `Ver ${ attachment.name }` }
                      className="attachment-gallery-thumbnail-select"
                      type="button"
                      onClick={() => setSelectedAttachmentPreviewIndex( index )}
                    >
                      <div className="attachment-gallery-thumbnail-media">
                        { renderAttachmentPreviewContent( attachment, url, true ) }
                      </div>
                    </button>

                    <IconButton
                      aria-label={ `Remover ${ attachment.name }` }
                      className="attachment-gallery-thumbnail-remove"
                      color="text"
                      shape={ Shape.CIRCLE }
                      onClick={( event ) => {
                        event.stopPropagation();
                        handleRemoveAttachment( index );
                        setSelectedAttachmentPreviewIndex(( current ) => {
                          const nextLength = attachmentPreviewItems.length - 1;
                          if( nextLength <= 0 ) return 0;
                          if( current > index ) return current - 1;
                          if( current >= nextLength ) return nextLength - 1;
                          return current;
                        });
                      }}
                    >
                      <X size={ 12 } weight="bold" />
                    </IconButton>
                  </div>
                ))
              }
            </div>
          )
        }
      </div>
    );
  }, [ attachmentPreviewItems, handleRemoveAttachment, selectedAttachmentPreviewIndex ]);

  return (
    <>
      <ChatsMain className={ selectedServiceChat ? "has-selected-chat" : "" }>
        <LeftView $hasSelectedChat={ selectedServiceChat !== null }>
          <LeftViewHead>
            <span>Conversas</span>

            <ChatListHeadActions>
              <IconButton
                color="text"
                onClick={ handleToggleOrder }
                aria-label={ order === Order.ASC ? "Ordenar conversas mais recentes primeiro" : "Ordenar conversas mais antigas primeiro" }
              >
                <AnimatePresence>
                  {
                    order === Order.ASC && (
                      <SortAscendingIcon
                        size={ 24 }
                        initial={{
                          opacity: 0,
                          scale: 0,
                        }}
                        animate={{
                          opacity: 1,
                          scale: 1,
                        }}
                        exit={{
                          opacity: 0,
                          scale: 0,
                        }}
                        transition={{ duration: .2 }}
                      />
                    )
                  }
                </AnimatePresence>

                <AnimatePresence>
                  {
                    order === Order.DESC && (
                      <SortDescendingIcon
                        size={ 24 }
                        initial={{
                          opacity: 0,
                          scale: 0,
                        }}
                        animate={{
                          opacity: 1,
                          scale: 1,
                        }}
                        exit={{
                          opacity: 0,
                          scale: 0,
                        }}
                        transition={{ duration: .2 }}
                      />
                    )
                  }
                </AnimatePresence>
              </IconButton>

              <AnimatePresence>
                {
                  ( showCleanButton ) && (
                    <motion.div
                      initial={{
                        transform: "translateX(100%)",
                        opacity: 0,
                        width: 0,
                      }}
                      animate={{
                        transform: "translateX(0%)",
                        opacity: 1,
                        width: 32,
                      }}
                      exit={{
                        transform: "translateX(100%)",
                        opacity: 0,
                        width: 0,
                      }}
                      transition={{ duration: .2 }}
                    >
                      <IconButton
                        color="error"
                        onClick={ cleanFilters }
                        aria-label="Limpar filtros de conversas"
                      >
                        <FunnelX
                          size={ 24 }
                        />
                      </IconButton>
                    </motion.div>
                  )
                }
              </AnimatePresence>

              <IconButton
                color="text"
                onClick={() => setShowMenuFilter(( value ) => !value )}
                aria-label={ showMenuFilter ? "Ocultar filtros de conversas" : "Mostrar filtros de conversas" }
                aria-expanded={ showMenuFilter }
                aria-controls="chat-filters"
              >
                <Funnel
                  size={ 24 }
                />
              </IconButton>

              <IconButton
                color="text"
                onClick={() => {}}
                aria-label="Iniciar nova conversa"
              >
                <Plus
                  size={ 24 }
                />
              </IconButton>
            </ChatListHeadActions>
          </LeftViewHead>

          <ChatSearchForm>
            <div className="inputs-view">
              <div className="input-view search-input-view">
                <MagnifyingGlass size={ 18 } aria-hidden="true" />

                <input
                  id="filter-services"
                  type="text"
                  placeholder="Buscar"
                  aria-label="Buscar conversas"
                  value={ search }
                  onChange={({ target: { value }}) => setSearch( value )}
                />
              </div>
            </div>
          </ChatSearchForm>

          <AnimatePresence>
            {
              showMenuFilter && (
                <FormAnimated
                  id="chat-filters"
                  style={{
                    gap: theme.spacing.xxx_small,
                    overflow: "hidden",
                  }}
                  initial={{
                    height: 0,
                  }}
                  animate={{
                    height: "auto",
                  }}
                  exit={{
                    height: 0,
                  }}
                  transition={{ duration: .2 }}
                >
                  <div className="inputs-view">
                    <div className="inputs-view-row">
                      <Select<Queue>
                        keyProperty="id"
                        name="queue"
                        label="Fila"
                        options={ queues }
                        selected={ queue }
                        handleSelect={( option ) => setQueue( option )}
                        RenderOption={( queue ) => <span>{ queue.name }</span> }
                        showError={ false }
                      />

                      <Select<Channel>
                        keyProperty="id"
                        name="channel"
                        label="Canal"
                        options={ channels }
                        selected={ channel }
                        handleSelect={( option ) => setChannel( option )}
                        RenderOption={( channel ) => <span>{ channel.name ?? "Canal" }</span> }
                        showError={ false }
                      />
                    </div>
                  </div>
                </FormAnimated>
              )
            }
          </AnimatePresence>

          <ChatListTypes>
            {
              serviceStatus.map(( _status ) => {
                if ( _status === "separator" ) {
                  return <hr key="service-status-separator" className="vertical-separator" />;
                }

                return (
                  <Button
                    key={ _status.value }
                    $size={ Size.SMALL }
                    $variant={ status === _status.value ? Variant.CONTAINED : Variant.OUTLINED }
                    onClick={() => setStatus( _status.value )}
                    $color="text"
                    aria-pressed={ status === _status.value }
                  >
                    { _status.label }

                    {
                      _status.badgeCount
                        ? (
                            <div className="badge">
                              <Badge
                                number={ _status.badgeCount }
                                color="success"
                              />
                            </div>
                          )
                        : <></>
                    }
                  </Button>
                );
              })
            }
          </ChatListTypes>

          <ServicesList>
            <ServicesScrollBarView
              ref={ scrollServicesRef }
              onScrollCapture={ onScrollServicesCapture }
              aria-busy={ loadingServices }
            >
              {
                serviceSearchOccurrenceItems.map(({ service, occurrence }) => {
                  const previewMessage = occurrence ?? service.chat.messages?.[ 0 ];
                  const previewMessageSenderLabel = getLastMessageSenderLabel( service, previewMessage );
                  return (
                    <ChatItem
                      key={ occurrence ? `${ service.id }_${ occurrence.id }` : service.id }
                      className={ `chat-item${ occurrence ? " search-occurrence" : "" }${ selectedServiceChat?.chat.id === service.chat.id ? " selected" : "" }${ service.unReadCount > 0 ? " unread" : "" }` }
                      aria-current={ selectedServiceChat?.chat.id === service.chat.id ? "true" : undefined }
                      onClick={() => handleSelectSearchOccurrence( service, occurrence )}
                    >
                      <div className="contact-view-info">
                        <div className="avatar-view">
                          <Avatar
                            size={ 40 }
                            name={ service.chat.contact.name }
                            url={ buildInlineFileURL( service.chat.contact.avatarUrl ?? service.chat.contact.avatar )}
                          />

                          {
                            service.chat.contact.isOnline
                              ? <span className="online-badge" />
                              : null
                          }
                        </div>

                        <div className="chat-content">
                          <strong>{ service.chat.contact.name }</strong>

                          {
                            previewMessage
                              ? <div className="last-message-view">
                                  {
                                    occurrence
                                      ? <></>
                                      : previewMessage.ownerType !== OwnerTypeMessage.CONTACT && previewMessage.messageType !== MessageType.INTERNAL
                                      ? getStatusIcon( previewMessage.status )
                                      : <></>
                                  }

                                  <div>
                                    { previewMessageSenderLabel ? `${ previewMessageSenderLabel }: ` : "" }
                                    { occurrence ? renderSearchOccurrencePreview( occurrence ) : renderLastMessage( previewMessage )}
                                  </div>
                                </div>
                              : <></>
                          }
                        </div>
                      </div>

                      <ChatContentInfo>
                        <div>
                          {
                            status === ServiceStatus.CONVERSATIONS
                              ? <></>
                              : <strong>{ service?.chat?.channel?.name ? service?.chat?.channel?.name : "" }{ service.queue?.name && service?.chat?.channel?.name ? " · " : "" }{ service.queue?.name ? service.queue.name : "" }</strong>
                          }

                          {
                            previewMessage
                              ? <span>{ formatRelativeDate( previewMessage.createdAt )}</span>
                              : <></>
                          }
                        </div>

                        {
                          status === ServiceStatus.CONVERSATIONS
                            ? <></>
                            : (
                                <div>
                                  {
                                    service.user?.name
                                      ? <strong>{ service.user.name }</strong>
                                      : <></>
                                  }
                                </div>
                              )
                        }
                      </ChatContentInfo>

                      <div className="badge">
                        <Badge
                          number={ service.unReadCount }
                          color="success"
                        />
                      </div>
                    </ChatItem>
                  );
                })
              }

              {
                loadingServices
                  ? <ServiceListSkeleton compact={ services.length > 0 } />
                  : <></>
              }

              {
                !loadingServices && serviceSearchOccurrenceItems.length === 0
                  ? (
                      <ListEmptyState role="status">
                        <ChatSlash size={ 28 } weight="duotone" />
                        <strong>Nenhuma conversa</strong>
                        <span>{ normalizedSearch ? "Tente outro termo ou ajuste os filtros." : "Não há conversas neste status." }</span>
                      </ListEmptyState>
                    )
                  : <></>
              }
            </ServicesScrollBarView>
          </ServicesList>
        </LeftView>

        <RightView $hasSelectedChat={ selectedServiceChat !== null }>
          {
            selectedServiceChat === null
              ? (
                  <ChatViewEmpty>
                    <img
                      src={ messagesIcon }
                      alt="messages"
                    />

                    <span>Escolha uma conversa a esquerda!</span>
                  </ChatViewEmpty>
                )
              : (
                  <ChatView>
                    <ChatTopBar>
                      <div className="contact-view">
                        <IconButton
                          className="back-button"
                          color="text"
                          title="Voltar para conversas"
                          aria-label="Voltar para conversas"
                          onClick={ handleCloseServiceChat }
                        >
                          <ArrowLeft
                            size={ 24 }
                          />
                        </IconButton>

                        <Avatar
                          size={ 32 }
                          name={ selectedServiceChat.chat.contact.name }
                          url={ buildInlineFileURL( selectedServiceChat.chat.contact.avatarUrl ?? selectedServiceChat.chat.contact.avatar )}
                        />

                        <div className="contact-info">
                          <strong>{ selectedServiceChat.chat.contact.name }</strong>

                          <div className="contact-meta">
                            <span>{ selectedServiceChat.chat?.channel?.name ? selectedServiceChat.chat.channel.name : "Canal" }{ selectedServiceChat.queue?.name ? ` · ${ selectedServiceChat.queue.name }` : "" }</span>

                            <AnimatePresence>
                              { selectedServiceChat.chat.contact.isOnline && <motion.span
                                key="online-status"
                                className="online-status"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{
                                  duration: .2,
                                }}
                              >
                                Online
                              </motion.span> }
                            </AnimatePresence>
                          </div>
                        </div>

                        <IconButton
                          color={ detailsOpen ? "info" : "text" }
                          title={ detailsOpen ? "Ocultar detalhes do contato" : "Mostrar detalhes do contato" }
                          aria-label={ detailsOpen ? "Ocultar detalhes do contato" : "Mostrar detalhes do contato" }
                          aria-expanded={ detailsOpen }
                          aria-controls="chat-contact-context"
                          onClick={() => setDetailsOpen(( open ) => !open )}
                        >
                          <AddressBook size={ 24 } weight={ detailsOpen ? "fill" : "regular" } />
                        </IconButton>

                        <IconButton
                          ref={ serviceMenuTriggerRef }
                          color="text"
                          onClick={ handleServiceMenuButtonClick }
                          stopPropagation={ showServiceMenu }
                          aria-label={ showServiceMenu ? "Fechar ações do atendimento" : "Abrir ações do atendimento" }
                          aria-expanded={ showServiceMenu }
                          aria-controls="service-actions-menu"
                        >
                          <AnimatePresence>
                            {
                              showServiceMenu
                                ? <AnimatedX
                                    key="service-menu-close"
                                    size={ 24 }
                                    style={ menuToggleIconStyle }
                                    initial={{
                                      opacity: 0,
                                      transform: "rotate(-90deg)",
                                    }}
                                    animate={{
                                      opacity: 1,
                                      transform: "rotate(0deg)",
                                    }}
                                    exit={{
                                      opacity: 0,
                                      transform: "rotate(-90deg)",
                                    }}
                                    transition={ menuToggleIconTransition }
                                  />
                                : <AnimatedDotsThreeVertical
                                    key="service-menu-open"
                                    size={ 24 }
                                    style={ menuToggleIconStyle }
                                    initial={{
                                      opacity: 0,
                                      transform: "rotate(90deg)",
                                    }}
                                    animate={{
                                      opacity: 1,
                                      transform: "rotate(0deg)",
                                    }}
                                    exit={{
                                      opacity: 0,
                                      transform: "rotate(90deg)",
                                    }}
                                    transition={ menuToggleIconTransition }
                                  />
                            }
                          </AnimatePresence>
                        </IconButton>
                      </div>
                    </ChatTopBar>

                    <ChatList>
                      <AnimatePresence>
                        {
                          showServiceMenu && (
                            <OutsideClick
                              executeCallback={ showServiceMenu }
                              callback={() => handleToggleShowServiceMenu( false )}
                            >
                              <ChatMenu
                                ref={ serviceMenuRef }
                                id="service-actions-menu"
                                role="menu"
                                aria-label="Ações do atendimento"
                                data-escape-layer="true"
                                initial={{
                                  height: 0,
                                  top: - theme.spacing.xx_medium,
                                }}
                                animate={{
                                  height: "auto",
                                  top: theme.spacing.xx_small,
                                }}
                                exit={{
                                  height: 0,
                                  top: - theme.spacing.xx_medium,
                                }}
                                transition={{
                                  duration: .2,
                                }}
                              >
                                {
                                  serviceMenuItems.filter(( item ) => hasRole( assignedRole, item.roles ) && item.show ).map(({ label, color, Icon, onClick }) => (
                                    <ChatMenuItem
                                      key={ label }
                                      $color={ color }
                                      onClick={ onClick }
                                      onMouseDown={( e ) => showServiceMenu ? e.stopPropagation() : {}}
                                      role="menuitem"
                                    >
                                      <Icon
                                        size={ 24 }
                                        weight="regular"
                                      />

                                      <span>{ label }</span>
                                    </ChatMenuItem>
                                  ))
                                }
                              </ChatMenu>
                            </OutsideClick>
                          )
                        }
                      </AnimatePresence>

                      <AnimatePresence>
                        {
                          showAttachmentMenu && (
                            <OutsideClick
                              executeCallback={ showAttachmentMenu }
                              callback={() => handleToggleShowAttachmentMenu( false )}
                            >
                              <ChatMenu
                                initial={{
                                  height: 0,
                                  bottom: - theme.spacing.xx_medium,
                                }}
                                animate={{
                                  height: "auto",
                                  bottom: theme.spacing.xx_small,
                                }}
                                exit={{
                                  height: 0,
                                  bottom: - theme.spacing.xx_medium,
                                }}
                                transition={{
                                  duration: .2,
                                }}
                                style={{
                                  right: "auto",
                                  left: theme.spacing.xx_medium,
                                }}
                              >
                                {
                                  attachmentMenuItems.map(({ label, Icon, onClick }) => (
                                    <ChatMenuItem
                                      key={ label }
                                      $color="text"
                                      onClick={ onClick }
                                      onMouseDown={( e ) => showAttachmentMenu ? e.stopPropagation() : {}}
                                    >
                                      <Icon
                                        size={ 24 }
                                        weight="regular"
                                      />

                                      <span>{ label }</span>

                                      <CaretDoubleRight
                                        size={ 16 }
                                        weight="light"
                                      />
                                    </ChatMenuItem>
                                  ))
                                }
                              </ChatMenu>
                            </OutsideClick>
                          )
                        }
                      </AnimatePresence>

                      <AnimatePresence>
                        {
                          showEmojiPicker && (
                            <OutsideClick
                              executeCallback={ showEmojiPicker }
                              callback={() => setShowEmojiPicker( false )}
                            >
                              <EmojiPickerCard
                                initial={{
                                  opacity: 0,
                                  y: theme.spacing.x_medium,
                                }}
                                animate={{
                                  opacity: 1,
                                  y: 0,
                                }}
                                exit={{
                                  opacity: 0,
                                  y: theme.spacing.x_medium,
                                }}
                                transition={{ duration: .16 }}
                              >
                                <div className="emoji-picker-head">
                                  <strong>Emojis</strong>

                                  <button
                                    type="button"
                                    aria-label="Fechar emojis"
                                    onClick={() => setShowEmojiPicker( false )}
                                  >
                                    <X size={ 18 } />
                                  </button>
                                </div>

                                <div className="emoji-picker-grid">
                                  {
                                    sendEmojiOptions.map(( emoji ) => (
                                      <button
                                        key={ emoji }
                                        type="button"
                                        aria-label={ `Enviar emoji ${ emoji }` }
                                        onClick={() => handleInsertEmoji( emoji )}
                                      >
                                        { emoji }
                                      </button>
                                    ))
                                  }
                                </div>
                              </EmojiPickerCard>
                            </OutsideClick>
                          )
                        }
                      </AnimatePresence>

                      <AnimatePresence>
                        {
                          showStickerPicker && (
                            <OutsideClick
                              executeCallback={ showStickerPicker }
                              callback={() => setShowStickerPicker( false )}
                            >
                              <StickerPickerCard
                                initial={{
                                  opacity: 0,
                                  y: theme.spacing.x_medium,
                                }}
                                animate={{
                                  opacity: 1,
                                  y: 0,
                                }}
                                exit={{
                                  opacity: 0,
                                  y: theme.spacing.x_medium,
                                }}
                                transition={{ duration: .16 }}
                              >
                                <div className="sticker-picker-head">
                                  <strong>Figurinhas</strong>

                                  <button
                                    type="button"
                                    aria-label="Fechar figurinhas"
                                    onClick={() => setShowStickerPicker( false )}
                                  >
                                    <X size={ 18 } />
                                  </button>
                                </div>

                                {
                                  savedStickers.length > 0
                                    ? (
                                        <div className="sticker-picker-grid">
                                          {
                                            savedStickers.map(( sticker ) => {
                                              const stickerURL = buildInlineFileURL( getStickerReference( sticker.fileMetadata ));

                                              return (
                                                <div
                                                  key={ sticker.id }
                                                  className="sticker-item"
                                                >
                                                  <button
                                                    type="button"
                                                    className="sticker-send"
                                                    aria-label={ `Enviar ${ sticker.name }` }
                                                    onClick={() => handleSendSticker( sticker )}
                                                  >
                                                    {
                                                      stickerURL
                                                        ? <img src={ stickerURL } alt={ sticker.name } />
                                                        : <span>Figurinha</span>
                                                    }
                                                  </button>

                                                  <button
                                                    type="button"
                                                    className="sticker-remove"
                                                    aria-label={ `Remover ${ sticker.name }` }
                                                    onClick={( event ) => {
                                                      event.stopPropagation();
                                                      handleRemoveSticker( sticker );
                                                    }}
                                                  >
                                                    <X size={ 12 } weight="bold" />
                                                  </button>
                                                </div>
                                              );
                                            })
                                          }
                                        </div>
                                      )
                                    : <span className="sticker-empty">Salve uma figurinha pelo menu da mensagem para enviar depois.</span>
                                }
                              </StickerPickerCard>
                            </OutsideClick>
                          )
                        }
                      </AnimatePresence>

                      <ChatScrollBarView
                        ref={ scrollMessagesRef }
                        onScrollCapture={ onScrollMessagesCapture }
                        role="log"
                        aria-live="polite"
                        aria-relevant="additions text"
                        aria-busy={ loadingMessages }
                        aria-label="Mensagens da conversa"
                      >
                        {
                          groupedMessages.map(({ date, messages }) => (
                            <DateGroupMessages
                              key={ date }
                            >
                              {
                                messages.filter(( message ) => message.messageType !== MessageType.REACTION ).map(( message, index, visibleMessages ) => (
                                  <Fragment
                                    key={ message.id }
                                    >
                                    {
                                      message.messageType === MessageType.INTERNAL
                                        ? (
                                            <InternalMessageRow
                                              data-message-id={ message.id }
                                              $selected={ selectedFeedbackMessageId === message.id }
                                            >
                                              <div className="internal-message-card">
                                                <div className="internal-message-header">
                                                  <NotePencil size={ 16 } weight="duotone" />
                                                  <strong>Comentário interno</strong>
                                                  <span>{ getUserNameByMessage( message )}</span>
                                                </div>

                                                <div className="internal-message-content">
                                                  <MessageTextContent content={ message.content } />
                                                </div>

                                                <span className="internal-message-time">{ format( message.createdAt, "HH:mm")}</span>
                                              </div>
                                            </InternalMessageRow>
                                          )
                                        : message.ownerType === OwnerTypeMessage.CONTACT
                                        ? (
                                            <MessageLeftRow
                                              data-message-id={ message.id }
                                              $show={ message.show }
                                              $selected={ selectedFeedbackMessageId === message.id || selectedMessages.has( message.id ) }
                                              $isSticker={ message.messageType === MessageType.STICKER }
                                              $isImageOrVideo={[ MessageType.IMAGE, MessageType.VIDEO ].includes( message.messageType ) && message.content === "" }
                                              onClick={() => handleMessageSelectionClick( message )}
                                              onDoubleClick={() => !multiSelectionMode && handleMessageDoubleClick( message )}
                                              onMouseLeave={ handleMessageRowLeave }
                                            >
                                              <div>
                                                {
                                                  message.show
                                                    ? (
                                                        <Avatar
                                                          name={ selectedServiceChat.chat.contact.name }
                                                          url={ buildInlineFileURL( selectedServiceChat.chat.contact.avatarUrl ?? selectedServiceChat.chat.contact.avatar )}
                                                          size={ 32 }
                                                        />
                                                      )
                                                    : <></>
                                                }
                                              </div>

                                              <div
                                                className="message-bubble"
                                                onDoubleClick={( event ) => event.stopPropagation()}
                                              >
                                                { renderMessageActions( message, "left" )}

                                                {
                                                  message.show
                                                    ? <div className="name-view">
                                                        <span className="name">{ selectedServiceChat.chat.contact.name }</span>
                                                      </div>
                                                    : <></>
                                                }

                                                <div className="message-content">
                                                  { message.isForwarded && <ForwardedLabel><ShareFat size={ 13 } weight="bold" /><span>Encaminhada</span></ForwardedLabel> }
                                                  { renderReplyPreview( message )}

                                                  { renderMessage( message )}
                                                </div>

                                                { renderReactions( message, "received" )}

                                                <div className="footer">
                                                  { renderMessageFooterMutation( message )}

                                                  <span className="time">{ format( message.createdAt, "HH:mm")}</span>
                                                </div>
                                              </div>
                                            </MessageLeftRow>
                                          )
                                        : (
                                            <MessageRightRow
                                              data-message-id={ message.id }
                                              $show={ message.show }
                                              $selected={ selectedFeedbackMessageId === message.id || selectedMessages.has( message.id ) }
                                              $isSticker={ message.messageType === MessageType.STICKER }
                                              $isImageOrVideo={[ MessageType.IMAGE, MessageType.VIDEO ].includes( message.messageType ) && message.content === "" }
                                              onClick={() => handleMessageSelectionClick( message )}
                                              onDoubleClick={() => !multiSelectionMode && handleMessageDoubleClick( message )}
                                              onMouseLeave={ handleMessageRowLeave }
                                            >
                                              <div>
                                                {
                                                  message.show
                                                    ? (
                                                        <Avatar
                                                          name={ getUserNameByMessage( message )}
                                                          size={ 32 }
                                                          url={ getProfile( message ) ?? null }
                                                        />
                                                      )
                                                    : <></>
                                                }
                                              </div>

                                              <div
                                                className="message-bubble"
                                                onDoubleClick={( event ) => event.stopPropagation()}
                                              >
                                                { renderMessageActions( message, "right" )}

                                                {
                                                  message.show
                                                    ? (
                                                        <div className="name-view">
                                                          <span className="name">{ getUserNameByMessage( message )}</span>
                                                        </div>
                                                      )
                                                    : <></>
                                                }

                                                <div className="message-content">
                                                  { message.isForwarded && <ForwardedLabel><ShareFat size={ 13 } weight="bold" /><span>Encaminhada</span></ForwardedLabel> }
                                                  { renderReplyPreview( message )}

                                                  { renderMessage( message )}

                                                  {
                                                    canRetryPersistedMessage( message ) && (
                                                      <PersistedFailure role="status">
                                                        <strong>Falha ao enviar</strong>
                                                        <button
                                                          type="button"
                                                          disabled={ retryingMessageIds.has( message.id )}
                                                          onClick={() => void handleRetryMessage( message )}
                                                        >
                                                          { retryingMessageIds.has( message.id ) ? "Tentando novamente..." : "Tentar novamente" }
                                                        </button>
                                                      </PersistedFailure>
                                                    )
                                                  }
                                                </div>

                                                { renderReactions( message, "sent" )}

                                                <div className="footer">
                                                  { renderMessageFooterMutation( message )}

                                                  <div className="status-view">
                                                    <span className="time">{ format( message.createdAt, "HH:mm")}</span>

                                                    { getStatusIcon( message.status )}
                                                  </div>
                                                </div>
                                              </div>
                                            </MessageRightRow>
                                          )
                                    }
                                  </Fragment>
                                ))
                              }

                              <DateGroupTitle>
                                <span>{ date }</span>
                              </DateGroupTitle>
                            </DateGroupMessages>
                          ))
                        }

                        {
                          loadingMessages && loadingMessagesPage === 1
                            ? <MessagesSkeleton compact={ groupedMessages.length > 0 } />
                            : <></>
                        }

                        <SpinLoading show={ loadingMessages && loadingMessagesPage > 1 } />
                      </ChatScrollBarView>

                      <AnimatePresence>
                        {
                          showScrollToBottom && (
                            <ScrollToBottom
                              aria-label="Ir para a mensagem mais recente"
                              initial={{
                                bottom: - 50,
                              }}
                              animate={{
                                bottom: - theme.spacing.x_small,
                              }}
                              exit={{
                                bottom: - 50,
                              }}
                              transition={{ duration: .2 }}
                              onClick={ handleScrollToBottom }
                            >
                              <CaretDoubleDown
                                size={ 24 }
                              />
                            </ScrollToBottom>
                          )
                        }
                      </AnimatePresence>
                    </ChatList>

                    <AnimatePresence>
                      {
                        attachments.length > 0 && (
                          <AttachmentView
                            initial={{
                              top: "auto",
                              height: 0,
                            }}
                            animate={{
                              top: 64,
                              height: "auto",
                            }}
                            exit={{
                              top: "auto",
                              height: 0,
                            }}
                            transition={{ duration: .2 }}
                          >
                            <div className="header">
                              <IconButton
                                color="text"
                                onClick={() => handleChangeAttachment( null )}
                                aria-label="Fechar pré-visualização dos anexos"
                              >
                                <X
                                  size={ 24 }
                                />
                              </IconButton>
                            </div>

                            <div className="body">
                              { previewContent }
                            </div>

                          </AttachmentView>
                        )
                      }
                    </AnimatePresence>

                    <ChatBottomBar
                      ref={ chatBottomBarRef }
                      $connected={ channelIsConnected || internalMessage }
                      $attachmentPreview={ attachments.length > 0 }
                    >
                      <div className="chat-bottom-bar">
                        <AttachmentInput
                          type="file"
                          ref={ attachmentInputRef }
                          multiple={ attachmentType !== "sticker" }
                          value={""}
                          onChange={( event ) => handleChangeAttachment( event.target.files )}
                        />

                        {
                          multiSelectionMode
                            ? renderMultiMessageSelectionBar()
                            : selectedHistoricalConversation
                            ? (
                                <Button
                                  $color="text"
                                  $variant={ Variant.CONTAINED }
                                  onClick={() => handleToggleShowStartConversationModal( true )}
                                >
                                  Iniciar atendimento
                                </Button>
                              )
                              : selectedServiceChat.status === ServiceStatus.FINALIZED
                            ? <span className="service-finalized">Atendimento finalizado</span>
                              : channelIsConnected || internalMessage
                                  ? (
                                      <>
                                        <AnimatePresence initial={ false }>
                                          {
                                            attachments.length === 0 && (
                                              <AttachmentActions
                                                key="attachment-actions"
                                                initial={{ opacity: 0, width: 0, x: -8, scale: .94 }}
                                                animate={{ opacity: 1, width: "auto", x: 0, scale: 1 }}
                                                exit={{ opacity: 0, width: 0, x: -8, scale: .94 }}
                                                transition={{ duration: .18, ease: "easeOut" }}
                                              >
                                                <IconButton
                                                  title="Comentário interno"
                                                  color={ internalMessage ? "warning" : "text" }
                                                  shape={Shape.CIRCLE}
                                                  onClick={ handleToggleInternalMessage }
                                                  aria-label={ internalMessage ? "Desativar comentário interno" : "Ativar comentário interno" }
                                                  aria-pressed={ internalMessage }
                                                >
                                                  <AnimatePresence initial={ false }>
                                                    {
                                                      internalMessage
                                                        ? <AnimatedX
                                                            key="internal-message-close"
                                                            size={ 24 }
                                                            style={ menuToggleIconStyle }
                                                            initial={{
                                                              opacity: 0,
                                                              transform: "rotate(-90deg)",
                                                            }}
                                                            animate={{
                                                              opacity: 1,
                                                              transform: "rotate(0deg)",
                                                            }}
                                                            exit={{
                                                              opacity: 0,
                                                              transform: "rotate(-90deg)",
                                                            }}
                                                            transition={ menuToggleIconTransition }
                                                          />
                                                        : <AnimatedNotePencil
                                                            key="internal-message-open"
                                                            size={ 24 }
                                                            weight="duotone"
                                                            style={ menuToggleIconStyle }
                                                            initial={{
                                                              opacity: 0,
                                                              transform: "rotate(90deg)",
                                                            }}
                                                            animate={{
                                                              opacity: 1,
                                                              transform: "rotate(0deg)",
                                                            }}
                                                            exit={{
                                                              opacity: 0,
                                                              transform: "rotate(90deg)",
                                                            }}
                                                            transition={ menuToggleIconTransition }
                                                          />
                                                    }
                                                  </AnimatePresence>
                                                </IconButton>

                                                {
                                                  !internalMessage
                                                    ? (
                                                        <IconButton
                                                  color="text"
                                                  shape={Shape.CIRCLE}
                                                  onClick={ handleAttachmentMenuButtonClick }
                                                  stopPropagation={ showAttachmentMenu }
                                                  aria-label={ showAttachmentMenu ? "Fechar menu de anexos" : "Abrir menu de anexos" }
                                                  aria-expanded={ showAttachmentMenu }
                                                >
                                                  <AnimatePresence>
                                                    {
                                                      showAttachmentMenu
                                                        ? <AnimatedX
                                                            key="attachment-menu-close"
                                                            size={ 24 }
                                                            style={ menuToggleIconStyle }
                                                            initial={{
                                                              opacity: 0,
                                                              transform: "rotate(-90deg)",
                                                            }}
                                                            animate={{
                                                              opacity: 1,
                                                              transform: "rotate(0deg)",
                                                            }}
                                                            exit={{
                                                              opacity: 0,
                                                              transform: "rotate(-90deg)",
                                                            }}
                                                            transition={ menuToggleIconTransition }
                                                          />
                                                        : <AnimatedPaperclip
                                                            key="attachment-menu-open"
                                                            size={ 24 }
                                                            weight="duotone"
                                                            style={ menuToggleIconStyle }
                                                            initial={{
                                                              opacity: 0,
                                                              transform: "rotate(90deg)",
                                                            }}
                                                            animate={{
                                                              opacity: 1,
                                                              transform: "rotate(0deg)",
                                                            }}
                                                            exit={{
                                                              opacity: 0,
                                                              transform: "rotate(90deg)",
                                                            }}
                                                            transition={ menuToggleIconTransition }
                                                          />
                                                    }
                                                  </AnimatePresence>
                                                </IconButton>
                                                      )
                                                    : <></>
                                                }

                                                <IconButton
                                                  color="text"
                                                  shape={Shape.CIRCLE}
                                                  onClick={ handleEmojiPickerButtonClick }
                                                  stopPropagation={ showEmojiPicker }
                                                  aria-label={ showEmojiPicker ? "Fechar seletor de emojis" : "Abrir seletor de emojis" }
                                                  aria-expanded={ showEmojiPicker }
                                                >
                                                  <AnimatePresence>
                                                    {
                                                      showEmojiPicker
                                                        ? <AnimatedX
                                                            key="emoji-picker-close"
                                                            size={ 24 }
                                                            style={ menuToggleIconStyle }
                                                            initial={{
                                                              opacity: 0,
                                                              transform: "rotate(-90deg)",
                                                            }}
                                                            animate={{
                                                              opacity: 1,
                                                              transform: "rotate(0deg)",
                                                            }}
                                                            exit={{
                                                              opacity: 0,
                                                              transform: "rotate(-90deg)",
                                                            }}
                                                            transition={ menuToggleIconTransition }
                                                          />
                                                        : <AnimatedSmiley
                                                            key="emoji-picker-open"
                                                            size={ 24 }
                                                            weight="duotone"
                                                            style={ menuToggleIconStyle }
                                                            initial={{
                                                              opacity: 0,
                                                              transform: "rotate(90deg)",
                                                            }}
                                                            animate={{
                                                              opacity: 1,
                                                              transform: "rotate(0deg)",
                                                            }}
                                                            exit={{
                                                              opacity: 0,
                                                              transform: "rotate(90deg)",
                                                            }}
                                                            transition={ menuToggleIconTransition }
                                                          />
                                                    }
                                                  </AnimatePresence>
                                                </IconButton>

                                                {
                                                  !internalMessage
                                                    ? (
                                                        <IconButton
                                                          color="text"
                                                          shape={Shape.CIRCLE}
                                                          onClick={ handleStickerPickerButtonClick }
                                                          stopPropagation={ showStickerPicker }
                                                          aria-label={ showStickerPicker ? "Fechar seletor de figurinhas" : "Abrir seletor de figurinhas" }
                                                          aria-expanded={ showStickerPicker }
                                                        >
                                                          <AnimatePresence>
                                                            {
                                                              showStickerPicker
                                                                ? <AnimatedX
                                                                    key="sticker-picker-close"
                                                                    size={ 24 }
                                                                    style={ menuToggleIconStyle }
                                                                    initial={{
                                                                      opacity: 0,
                                                                      transform: "rotate(-90deg)",
                                                                    }}
                                                                    animate={{
                                                                      opacity: 1,
                                                                      transform: "rotate(0deg)",
                                                                    }}
                                                                    exit={{
                                                                      opacity: 0,
                                                                      transform: "rotate(-90deg)",
                                                                    }}
                                                                    transition={ menuToggleIconTransition }
                                                                  />
                                                                : <AnimatedSticker
                                                                    key="sticker-picker-open"
                                                                    size={ 24 }
                                                                    weight="duotone"
                                                                    style={ menuToggleIconStyle }
                                                                    initial={{
                                                                      opacity: 0,
                                                                      transform: "rotate(90deg)",
                                                                    }}
                                                                    animate={{
                                                                      opacity: 1,
                                                                      transform: "rotate(0deg)",
                                                                    }}
                                                                    exit={{
                                                                      opacity: 0,
                                                                      transform: "rotate(90deg)",
                                                                    }}
                                                                    transition={ menuToggleIconTransition }
                                                                  />
                                                            }
                                                          </AnimatePresence>
                                                        </IconButton>
                                                      )
                                                    : <></>
                                                }
                                              </AttachmentActions>
                                            )
                                          }
                                        </AnimatePresence>

                                        <div className="composer-main">
                                          <AnimatePresence initial={ false }>
                                            {
                                              internalMessage
                                                ? <motion.div
                                                    key="internal-composer-banner"
                                                    className="internal-composer-banner-shell"
                                                    initial={{ opacity: 0, height: 0, y: -4 }}
                                                    animate={{ opacity: 1, height: "auto", y: 0 }}
                                                    exit={{ opacity: 0, height: 0, y: -4 }}
                                                    transition={{ duration: .18, ease: "easeOut" }}
                                                  >
                                                    <span className="internal-composer-banner">Comentário interno: Não será enviado ao cliente</span>
                                                  </motion.div>
                                                : <></>
                                            }
                                          </AnimatePresence>

                                          {
                                            internalMessage
                                              ? <></>
                                              : replyTargetMessage
                                              ? renderReplyPreviewCard( replyTargetMessage, handleClearReplyToMessage, true )
                                              : editTargetMessage
                                                ? renderReplyPreviewCard( editTargetMessage, clearEditTargetMessage, true, undefined, "Cancelar edição", "edit" )
                                                : <></>
                                          }

                                          <InputTextView
                                            editor={ editor }
                                          />

                                          <BubbleMenu
                                            className="bubble-menu"
                                            editor={ editor }
                                          >
                                            <Toolbar role="toolbar" aria-label="Formatação da mensagem">
                                              <Button
                                                $color={ editor?.isActive("bold") ? "info" : "text"}
                                                onClick={() => editor?.chain().focus().toggleBold().run()}
                                                $variant={ Variant.OUTLINED }
                                                $size={ Size.VERY_SMALL }
                                                aria-label="Negrito"
                                                aria-pressed={ Boolean( editor?.isActive("bold"))}
                                              >
                                                <TextB
                                                  size={ 16 }
                                                />
                                              </Button>

                                              <Button
                                                $color={ editor?.isActive("italic") ? "info" : "text"}
                                                onClick={() => editor?.chain().focus().toggleItalic().run()}
                                                $variant={ Variant.OUTLINED }
                                                $size={ Size.VERY_SMALL }
                                                aria-label="Itálico"
                                                aria-pressed={ Boolean( editor?.isActive("italic"))}
                                              >
                                                <TextItalic
                                                  size={ 16 }
                                                />
                                              </Button>

                                              <Button
                                                $color={ editor?.isActive("strike") ? "info" : "text"}
                                                onClick={() => editor?.chain().focus().toggleStrike().run()}
                                                $variant={ Variant.OUTLINED }
                                                $size={ Size.VERY_SMALL }
                                                aria-label="Tachado"
                                                aria-pressed={ Boolean( editor?.isActive("strike"))}
                                              >
                                                <TextStrikethrough
                                                  size={ 16 }
                                                />
                                              </Button>

                                              <Button
                                                $color={ editor?.isActive("code") ? "info" : "text"}
                                                onClick={() => editor?.chain().focus().toggleCode().run()}
                                                $variant={ Variant.OUTLINED }
                                                $size={ Size.VERY_SMALL }
                                                aria-label="Código"
                                                aria-pressed={ Boolean( editor?.isActive("code"))}
                                              >
                                                <Code
                                                  size={ 16 }
                                                />
                                              </Button>

                                              <Button
                                                $color={editor?.isActive("variable") ? "info" : "text"}
                                                onClick={() => editor?.commands.insertVariable()}
                                                $variant={ Variant.OUTLINED }
                                                $size={ Size.VERY_SMALL }
                                                aria-label="Inserir variável"
                                                aria-pressed={ Boolean( editor?.isActive("variable"))}
                                              >
                                                <BracketsCurly
                                                  size={ 16 }
                                                />
                                              </Button>
                                            </Toolbar>
                                          </BubbleMenu>
                                        </div>

                                        <IconButton
                                          color="text"
                                          shape={Shape.CIRCLE}
                                          onClick={() => handleSendMessage( message, attachments, attachmentType )}
                                          aria-label="Enviar mensagem"
                                        >
                                          <PaperPlaneTilt
                                            size={ 24 }
                                          />
                                        </IconButton>
                                      </>
                                    )
                                  : (
                                      <div className="disconnected-actions">
                                        <span className="disconnected">Channel desconectado.</span>
                                        <Button
                                          $color="warning"
                                          $variant={ Variant.CONTAINED }
                                          onClick={ handleToggleInternalMessage }
                                        >
                                          Comentário interno
                                        </Button>
                                      </div>
                                    )
                        }
                      </div>
                    </ChatBottomBar>
                  </ChatView>
                )
          }
        </RightView>

        {
          selectedServiceChat
            ? (
                <>
                  <ContextPanelBackdrop
                    type="button"
                    aria-label="Fechar detalhes do contato"
                    tabIndex={ -1 }
                    $open={ detailsOpen }
                    onClick={() => setDetailsOpen( false )}
                  />

                  <ContextPanel
                    ref={ contextPanelRef }
                    id="chat-contact-context"
                    $open={ detailsOpen }
                    aria-hidden={ !detailsOpen }
                    role={ !desktopDetails && detailsOpen ? "dialog" : undefined }
                    aria-modal={ !desktopDetails && detailsOpen ? true : undefined }
                    aria-labelledby={ !desktopDetails && detailsOpen ? "chat-contact-context-title" : undefined }
                  >
                    <div className="context-head">
                      <div>
                        <span>CONTEXTO</span>
                        <strong id="chat-contact-context-title">Detalhes do atendimento</strong>
                      </div>

                      <IconButton
                        color="text"
                        title="Fechar detalhes do contato"
                        aria-label="Fechar detalhes do contato"
                        tabIndex={ detailsOpen ? 0 : -1 }
                        onClick={() => setDetailsOpen( false )}
                      >
                        <X size={ 22 } />
                      </IconButton>
                    </div>

                    <div className="context-profile">
                      <Avatar
                        size={ 72 }
                        name={ selectedServiceChat.chat.contact.name }
                        url={ buildInlineFileURL( selectedServiceChat.chat.contact.avatarUrl ?? selectedServiceChat.chat.contact.avatar )}
                      />

                      <div>
                        <strong>{ selectedServiceChat.chat.contact.name }</strong>
                        <span>{ formatPhoneByDDI( selectedServiceChat.chat.contact.phone || selectedServiceChat.chat.phone ) || "Telefone não informado" }</span>
                      </div>

                      <span className={ `context-status ${ selectedServiceChat.status.toLocaleLowerCase() }` }>
                        { serviceStatusLabels[ selectedServiceChat.status ] }
                      </span>
                    </div>

                    <div className="context-section">
                      <span className="context-section-title">ATENDIMENTO</span>

                      <ContextField>
                        <span>Canal</span>
                        <strong>{ selectedServiceChat.chat.channel?.name || "Não informado" }</strong>
                      </ContextField>

                      <ContextField>
                        <span>Fila</span>
                        <strong>{ selectedServiceChat.queue?.name || "Não informada" }</strong>
                      </ContextField>

                      <ContextField>
                        <span>Responsável</span>
                        <strong>{ selectedServiceChat.user?.name || "Não atribuído" }</strong>
                      </ContextField>

                      <ContextField>
                        <span>Status</span>
                        <strong>{ serviceStatusLabels[ selectedServiceChat.status ] }</strong>
                      </ContextField>
                    </div>

                    <div className="context-section">
                      <span className="context-section-title">CONTATO</span>

                      <ContextField>
                        <span>Telefone</span>
                        <strong>{ formatPhoneByDDI( selectedServiceChat.chat.contact.phone || selectedServiceChat.chat.phone ) || "Não informado" }</strong>
                      </ContextField>

                      <ContextField>
                        <span>Organização</span>
                        <strong>{ selectedServiceChat.chat.contact.organization?.name || selectedServiceChat.queue?.organization?.name || "Não informada" }</strong>
                      </ContextField>
                    </div>
                  </ContextPanel>
                </>
              )
            : <></>
        }
      </ChatsMain>

      <ConfirmModal
        type="info"
        open={ showTransferModalConfirm }
        onClose={() => handleToggleShowTransferModalConfirm( false )}
        title="Transferir"
        question="Deseja realmente transferir este atendimento?"
        acceptText="Sim"
        acceptFunction={() => handleToggleShowTransferServiceModal( true )}
        declineText="Não"
        declineFunction={() => handleToggleShowTransferModalConfirm( false )}
      />

      <ConfirmModal
        type="warning"
        open={ showAbandonModalConfirm }
        onClose={() => handleToggleShowAbandonModalConfirm( false )}
        title="Abandonar"
        question="Deseja realmente abandonar este atendimento?"
        acceptText="Sim"
        acceptFunction={ handleAbandonService }
        declineText="Não"
        declineFunction={() => handleToggleShowAbandonModalConfirm( false )}
      />

      <CloseServiceModal
        open={ showCloseModalConfirm }
        onClose={() => handleToggleShowCloseModalConfirm( false )}
        onConfirm={ handleCloseService }
      />

      <ConfirmModal
        type="error"
        open={ Boolean( deleteTargetMessage )}
        onClose={() => setDeleteTargetMessage( null )}
        title="Excluir mensagem"
        question="Deseja realmente excluir esta mensagem?"
        acceptText="Sim"
        acceptFunction={ handleConfirmDeleteMessage }
        declineText="Não"
        declineFunction={() => setDeleteTargetMessage( null )}
      />

      {
        selectedServiceChat && (
          <TransferServiceModal
            open={ showTransferServiceModal }
            onClose={() => handleToggleShowTransferServiceModal( false )}
            onTransfer={ handleTransferService }
            service={ selectedServiceChat }
          />
        )
      }

      {
        selectedServiceChat && (
          <TransferServiceModal
            open={ showStartConversationModal }
            onClose={() => handleToggleShowStartConversationModal( false )}
            onTransfer={ handleStartSelectedConversation }
            service={ selectedServiceChat }
            title="Iniciar atendimento"
            submitText="Iniciar"
            showComment={ false }
          />
        )
      }

      <ContactModal
        open={ Boolean( sharedContactDraft )}
        onClose={() => setSharedContactDraft( null )}
        contact={ null }
        initialContact={ sharedContactDraft }
      />

      <ForwardMessageModal
        open={ forwardTargetMessages.length > 0 }
        onClose={() => {
          setForwardTargetMessages( [] );
          if( multiSelectionMode === "forward" ) cancelMultiSelection();
        }}
        sourceChatId={ selectedServiceChat?.chat.id ?? "" }
        messages={ forwardTargetMessages }
      />
    </>
  );
};
